import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { Search, ChevronDown, ChevronUp, X, ChevronRight, Loader, Download } from 'lucide-react';
import styled, { keyframes } from 'styled-components';
import { useData } from '../context/DataContext';
import { governmentHierarchy, topicHierarchy } from '../utils/hierarchyMaps';

// Styled components
const Container = styled.div`
  width: 100%;
  padding: 0;
  margin: 0;
  position: relative;
  min-height: 80vh; /* Changed from height: 100% to ensure it fills most of the viewport height */
  overflow-y: auto;
  scroll-behavior: smooth;
  background-color: #f8f9fa; /* Match the HomeContainer background color */
`;

const ControlsContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  margin-bottom: 24px;
  gap: 16px;
  width: 100%;
  
  .row-container {
    display: flex;
    flex: 0 1 auto;
    min-width: 250px;
    margin: 0;
    align-items: center;
    padding: 0;
    gap: 12px;
  }

  .search-bar-row {
    display: flex;
    flex-wrap: wrap;
    min-width: 100%;
    margin: 0;
    align-items: center;
    padding: 0;
    gap: 12px;
    row-gap: 16px;
  }

  .dropdown-row {
    display: flex;
    flex: 0 1 auto;
    min-width: 250px;
    margin: 0;
    align-items: center;
    padding: 0;
    gap: 12px;
  }

  .row-container-button-filters {
    display: flex;
    flex: 0 1 auto;
    min-width: 250px;
    margin: 0 0 12px 0;
    align-items: center;
    padding: 0;
    gap: 12px;
  }
  
  .filter-row {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    width: 100%;
  }
  
  .label-container {
    min-width: 90px;
    text-align: right;
    padding-right: 0px;
  }
  
  .label-container span {
    white-space: nowrap;
    display: inline-block;
    font-size: 14px;
    color: #6b7280;
  }
  
  .button-container {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    flex: 1;
    
    @media (max-width: 992px) {
      gap: 6px;
    }
  }
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
    gap: 16px;
    width: 100%;
    
    .row-container {
      flex: 1 0 100%;
      min-width: 100%;
      flex-direction: column;
      align-items: flex-start;
      padding: 0;
      width: 100%;
    }

    .search-bar-row {
      flex: 1 0 100%;
      row-gap: 12px;
    }

    .dropdown-row {
      flex: 1 0 100%;
      min-width: 100%;
      flex-direction: column;
      align-items: flex-start;
      padding: 0;
      width: 100%;
    }

    .row-container-button-filters {
      flex: 1 0 100%;
      min-width: 100%;
      flex-direction: column;
      align-items: flex-start;
      padding: 0;
      width: 100%;
    }
    
    .label-container {
      width: 100%;
      text-align: left;
      padding: 0 0 8px 0;
      min-width: auto;
    }
    
    .button-container {
      width: 100%;
    }

    .filter-row {
      flex-direction: column;
      gap: 16px;
    }
  }
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 12px 16px 12px 48px;
  padding-right: 40px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 16px;
  outline: none;
  background-color: white;
  transition: border-color 0.2s ease;
  position: relative;
  
  &:focus {
    border-color: #9ca3af;
    box-shadow: 0 0 0 2px rgba(209, 213, 219, 0.2);
  }
  
  @media (max-width: 768px) {
    font-size: 18px;
    padding: 14px 16px 14px 48px;
    padding-right: 40px;
    width: 100%;
  }
`;

const SearchIconWrapper = styled.div`
  position: absolute;
  left: 16px;
  top: 50%;
  transform: translateY(-50%);
  color: #6b7280;
  z-index: 10;
  pointer-events: none;
`;

const SearchClearButton = styled.div`
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  cursor: pointer;
  color: #6b7280;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  border-radius: 50%;
  background-color: #f3f4f6;
  
  &:hover {
    background-color: #e5e7eb;
    color: #4b5563;
  }
`;

// Add styled component for the tag
const TagBase = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 4px 8px;
  font-size: 13px;
  font-weight: 500;
  border-radius: 4px;
  background-color: ${props => props.$bgColor || '#e5e7eb'};
  color: ${props => props.$textColor || '#374151'};
  border: 1px solid ${props => props.$borderColor || 'transparent'};
  white-space: ${props => props.$nowrap ? 'nowrap' : 'normal'};
  overflow: hidden;
  text-overflow: ellipsis;
  cursor: ${props => props.onClick ? 'pointer' : 'default'};
  
  &:hover {
    opacity: ${props => props.onClick ? 0.9 : 1};
  }
  
  @media (max-width: 768px) {
    font-size: 12px;
  }
`;

// Wrap the styled component in a forwardRef
const Tag = React.forwardRef(({ children, $bgColor, $textColor, $nowrap, onClick, tagType, ...props }, ref) => {
  // Default border color if not provided
  const borderColor = props.border || 
    ($bgColor && $bgColor !== '#e5e7eb' ? $bgColor : '#d1d5db');
  
  return (
    <TagBase
      ref={ref}
      $bgColor={$bgColor}
      $textColor={$textColor}
      $borderColor={borderColor}
      $nowrap={$nowrap}
      onClick={onClick}
      {...props}
    >
      {children}
    </TagBase>
  );
});

const DetailPanel = styled.div`
  position: fixed;
  top: var(--navbar-height, 60px); /* Updated to match the new navbar height variable */
  right: 0;
  width: 100%;
  max-width: 500px;
  height: calc(100vh - var(--navbar-height, 60px)); /* Updated to match the new navbar height variable */
  background-color: white;
  border-left: 1px solid #373737;
  border-top: none; /* Ensure no top border creates a gap */
  z-index: 990; /* High but below navbar */
  padding: 24px;
  overflow-y: auto;
  transform: translateX(${props => props.isVisible ? '0' : '100%'});
  transition: transform 0.3s cubic-bezier(0.16, 1, 0.3, 1);
  will-change: transform;
  box-sizing: border-box;
  margin-top: 0; /* Ensure no margin creates a gap */
  box-shadow: ${props => props.isVisible ? '-5px 0 15px rgba(0, 0, 0, 0.1)' : 'none'};
  
  @media (max-width: 768px) {
    max-width: 100%;
    padding: 16px;
    top: var(--navbar-height-mobile, 60px); /* Adjusted to match CSS variable value */
    height: calc(100vh - var(--navbar-height-mobile, 60px)); /* Adjusted to match CSS variable value */
  }
`;

// Add an overlay for when the detail panel is open
const DetailPanelOverlay = styled.div`
  position: fixed;
  top: var(--navbar-height, 60px); /* Updated to match the new navbar height variable */
  left: 0;
  width: 100%;
  height: calc(100vh - var(--navbar-height, 60px)); /* Updated to match the new navbar height variable */
  background-color: rgba(0, 0, 0, 0.1); /* Add a slight background color to indicate overlay */
  z-index: 989; /* Just below the panel */
  opacity: ${props => props.isVisible ? 1 : 0};
  visibility: ${props => props.isVisible ? 'visible' : 'hidden'};
  transition: opacity 0.3s ease-in-out, visibility 0.3s ease-in-out;
  cursor: pointer;
  box-sizing: border-box;  
  pointer-events: none; /* Allow interaction with underlying content */
  
  
  /* Make the right portion of the overlay where the panel sits non-interactive */
  &::after {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    width: 500px; /* Match the panel width */
    height: 100%;
    pointer-events: none;
  }
  
  @media (max-width: 768px) {
    top: var(--navbar-height-mobile, 60px); /* Adjusted to match CSS variable value */
    height: calc(100vh - var(--navbar-height-mobile, 60px)); /* Adjusted to match CSS variable value */
    
    &::after {
      width: 100%; /* Full width on mobile */
    }
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6b7280;
  padding: 8px;
  position: absolute;
  top: 24px;
  right: 24px;
  z-index: 1;
  
  &:hover {
    color: #111827;
  }

  @media (max-width: 768px) {
    top: 16px;
    right: 16px;
  }
`;

const DetailTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 600;
  color: #111827;
  margin: 0;
  margin-bottom: 12px;
  padding-right: 48px; /* Increased space for the X button on the right */
  word-break: break-word;
`;

const DetailSection = styled.div`
  margin-bottom: 24px;
`;

const DetailLabel = styled.div`
  font-size: 12px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #6b7280;
  margin-bottom: 8px;
`;

const DetailValue = styled.div`
  font-size: 16px;
  font-weight: 400;
  color: #6b7280;
  line-height: 1.5;

  ul {
    margin: 0;
    padding-left: 20px;
    list-style-type: disc;
  }

  li {
    margin-bottom: 4px;
    display: list-item;
  }
`;

// Add a styled component for the action plan button
const ActionPlanButton = styled.a`
  display: block;
  width: 100%;
  padding: 12px 16px;
  background-color: #ae7cef;
  color: white;
  text-align: center;
  font-weight: 500;
  border-radius: 6px;
  text-decoration: none;
  margin-top: 24px;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  opacity: ${props => props.disabled ? 0.7 : 1};
  transition: background-color 0.2s;
  
  &:hover {
    background-color: ${props => props.disabled ? '#ae7cef' : '#9a64dc'};
  }
`;

const GroupSection = styled.div`
  background-color: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  overflow: visible;
  margin-bottom: 16px;
  width: 100%;
  min-width: 800px;
  box-sizing: border-box;
  position: relative;
  
  @media (max-width: 768px) {
    min-width: 100%;
  }
`;

const GroupHeaderBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background-color: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
  cursor: pointer;
  width: 100%;
  min-width: 800px;
  box-sizing: border-box;
  
  @media (max-width: 768px) {
    min-width: 100%;
  }
  
  &:hover {
    background-color: #f3f4f6;
  }
`;

const GroupHeaderLeft = styled.div`
  display: flex;
  align-items: center;
`;

const GroupCount = styled.div`
  font-size: 14px;
  color: #6b7280;
`;

const ErrorMessage = styled.div`
  padding: 16px;
  background-color: #fee2e2;
  border: 1px solid #ef4444;
  border-radius: 8px;
  color: #b91c1c;
  margin-bottom: 16px;
`;

// Animation for indeterminate progress bar
const pulseAnimation = keyframes`
  0% {
    background-position: 0% 0%;
  }
  100% {
    background-position: 100% 0%;
  }
`;

const LoadingOverlay = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  min-height: 80vh; /* Changed from 300px to ensure it fills most of the viewport height */
  width: 100%;
  padding: 48px 0;
  color: #6b7280;
  background-color: #f8f9fa; /* Match the HomeContainer background color */
  
  p {
    margin-top: 16px;
  }
`;

const ProgressBarContainer = styled.div`
  width: 300px;
  height: 12px;
  background-color: #e5e7eb;
  border-radius: 6px;
  overflow: hidden;
  margin-bottom: 16px;
`;

const ProgressBarFill = styled.div`
  height: 100%;
  background-color: #3b82f6;
  border-radius: 6px;
  transition: width 0.2s ease-in-out;
  
  &.indeterminate {
    animation: ${pulseAnimation} 1.5s infinite ease-in-out;
    background: linear-gradient(to right, #3b82f6, #60a5fa, #3b82f6);
    background-size: 200% 100%;
  }
`;

const CountText = styled.div`
  font-size: 14px;
  color: #6b7280;
  margin-bottom: 8px;
  
  @media (max-width: 768px) {
    font-size: 13px;
    margin-bottom: 4px;
  }
`;

const ClearFiltersLink = styled.span`
  color: #ae7cef;
  cursor: pointer;
  margin-left: 4px;
  font-weight: 500;
  
  &:hover {
    color: #9a64dc;
    text-decoration: underline;
  }
`;

const DropdownButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  font-size: 14px;
  font-weight: 400;
  border-radius: 6px;
  border: 1px solid #d1d5db;
  background-color: white;
  cursor: pointer;
  transition: all 0.2s;
  width: auto;
  min-width: 140px;
  max-width: max-content;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  
  > div {
    min-width: 0;
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    margin-right: 8px;
  }
  
  &:hover {
    background-color: #f9fafb;
  }
  
  ${({ $active }) => $active && `
    background-color: #f3f4f6;
    border-color: #9ca3af;
  `}
  
  .badge {
    background-color: #e5e7eb;
    color: #4b5563;
    border-radius: 99px;
    padding: 0 8px;
    font-size: 12px;
    margin-left: 8px;
    flex-shrink: 0;
  }
  
  .clear-button {
    background: none;
    border: none;
    margin-left: 8px;
    padding: 2px;
    cursor: pointer;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    
    &:hover {
      background-color: #e5e7eb;
    }
  }
  
  @media (max-width: 768px) {
    width: 100%;
    min-width: unset;
    max-width: 100%;
    height: 42px;
    padding: 8px 16px;
    
    .badge {
      font-size: 13px;
      padding: 1px 8px;
    }
  }
`;

const DropdownMenu = styled.div`
  position: absolute;
  top: 40px;
  left: auto;
  right: 0; /* Align to the right edge of the button container */
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  border: 1px solid #e5e7eb;
  z-index: 30;
  max-height: 400px;
  overflow-y: auto;
  width: 300px;
  
  @media (max-width: 768px) {
    width: 100%;
    position: fixed;
    top: auto;
    bottom: 0;
    left: 0;
    right: 0;
    max-height: 85vh;
    border-radius: 12px 12px 0 0;
    box-shadow: 0 -4px 12px rgba(0, 0, 0, 0.15);
    border: none;
    border-top: 1px solid #e5e7eb;
  }
`;

// Add a FilterCloseButton styled component after FilterDropdownMenu
const FilterDropdownMenu = styled.div`
  position: absolute;
  top: 42px;
  ${props => props.$alignLeft ? 'left: 0; right: auto;' : 'right: 0;'}
  width: 300px;
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  border: 1px solid #e5e7eb;
  z-index: 31;
  overflow: hidden;
  
  @media (max-width: 768px) {
    width: 100%;
    position: fixed;
    top: auto;
    bottom: 0;
    left: 0;
    right: 0;
    border-radius: 12px 12px 0 0;
    box-shadow: 0 -4px 12px rgba(0, 0, 0, 0.1);
    max-height: 85vh;
    overflow-y: auto;
    border: none;
    border-top: 1px solid #e5e7eb;
  }
`;

// Updated Filter Container for the new tag-style layout - modified to have one row
const FilterTagsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  width: 100%;
  margin: 2px 0 14px 0;
  padding: 0;
  align-items: center;
  
  @media (max-width: 768px) {
    gap: 6px;
    margin: 0 0 12px 0;
  }
`;

/*
// Add back the category label
const CategoryLabel = styled.div`
  font-size: 13px;
  font-weight: 500;
  color: #6b7280;
  margin-right: 8px;
  padding-left: ${props => props.$first ? '0' : '16px'};
  border-left: ${props => props.$first ? 'none' : '1px solid #d1d5db'};
  
  @media (max-width: 768px) {
    font-size: 14px;
    padding-left: ${props => props.$first ? '0' : '12px'};
  }
`;
*/

/*
// Updated TagStyleFilterButton - keep colors only when active
const TagStyleFilterButton = styled.button`
  display: inline-block;
  align-items: center;
  padding: 6px 10px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 400;
  background-color: ${props => props.$active ? props.$bgColor : 'white'};
  color: ${props => props.$active ? props.$textColor : '#374151'};
  border: 1px solid #d1d5db;
  cursor: pointer;
  min-width: fit-content;
  max-width: max-content;
  transition: all 0.2s;
  white-space: nowrap;
  box-shadow: ${props => props.$active ? '0 1px 2px rgba(0, 0, 0, 0.05)' : 'none'};
  height: 28px;
  line-height: 1;
  
  &:hover {
    background-color: ${props => props.$active ? props.$bgColor : '#f9fafb'};
    opacity: ${props => props.$active ? 0.9 : 1};
  }
  
  &:active {
    transform: translateY(1px);
  }
  
  .clear-icon {
    display: inline-block;
    margin-left: 6px;
    vertical-align: middle;
    
    &:hover {
      color: #4b5563;
    }
  }
  
  @media (max-width: 768px) {
    height: 32px;
    font-size: 13px;
    padding: 8px 10px;
  }
`;
*/


const FilterItemsContainer = styled.div`
  max-height: 240px;
  overflow-y: auto;
  padding: 8px 0 16px;
  
  &::-webkit-scrollbar {
    width: 4px;
  }
  
  &::-webkit-scrollbar-track {
    background: #f9fafb;
  }
  
  &::-webkit-scrollbar-thumb {
    background-color: #d1d5db;
    border-radius: 4px;
  }
  
  @media (max-width: 768px) {
    max-height: 260px;
    padding: 8px 0 20px;
  }
`;

const FilterItem = styled.div`
  display: flex;
  align-items: center;
  padding: 6px 16px;
  border-radius: 4px;
  margin-bottom: 2px;
  
  &:hover {
    background-color: #f9fafb;
  }
  
  label {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    margin-left: 10px;
    font-size: 14px;
    cursor: pointer;
    color: #374151;
  }
  
  input {
    cursor: pointer;
  }
  
  @media (max-width: 768px) {
    padding: 8px 16px;
    
    label {
      font-size: 14px;
      margin-left: 12px;
    }
    
    input {
      width: 14px;
      height: 14px;
    }
  }
`;

const FilterSearchInput = styled.input`
  width: calc(100% - 32px);
  margin: 0 16px;
  padding: 8px 12px;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  font-size: 14px;
  margin-bottom: 8px;
  
  &:focus {
    outline: none;
    border-color: #a855f7;
    box-shadow: 0 0 0 1px rgba(168, 85, 247, 0.1);
  }
  
  @media (max-width: 768px) {
    padding: 10px 14px;
    font-size: 15px;
    margin-bottom: 12px;
    border-radius: 8px;
  }
`;

const DropdownItem = styled.div`
  padding: 8px 16px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 400;
  
  &:hover {
    background-color: #f9fafb;
  }
  
  &.selected {
    background-color: #f3f4f6;
  }
  
  display: flex;
  align-items: center;
  justify-content: space-between;
  
  @media (max-width: 768px) {
    padding: 14px 16px;
    font-size: 15px;
    border-bottom: 1px solid #f3f4f6;
    
    &:last-child {
      border-bottom: none;
    }
  }
`;

// Add a helper function to safely render field values
const safeRenderValue = (value) => {
  // Return empty string for null/undefined
  if (value === null || value === undefined) {
    return '';
  }
  
  // If it's an object (and somehow got through our data processing)
  if (typeof value === 'object') {
    // If it's an array, join elements
    if (Array.isArray(value)) {
      return value.map(v => String(v)).join(', ');
    }
    // If it has a URL (attachment), return that
    if (value.url) {
      return value.url;
    }
    // Last resort - stringify the object (but don't show in UI)
    return '';
  }
  
  // For normal strings, numbers, etc.
  return String(value);
};

// Add the getAdjustedColumnWidths function
const getAdjustedColumnWidths = (baseWidths, windowWidth, groupBy) => {
  // First adjust for screen size 
  let widths = {...baseWidths};
  
  // For small screens, prioritize recommendation and reduce other columns
  if (windowWidth < 768) {
    widths.recommendation = 30;
    widths.organization = 20;
    widths.details = 50;
    widths.topic = 0;
    widths.assignee = 0;
  }
  // For medium screens
  else if (windowWidth < 1200) {
    widths.recommendation = 20;
    widths.organization = 15;
    widths.details = 40;
    widths.topic = 15;
    widths.assignee = 10;
  }
  
  // Then adjust for groupBy mode
  const adjustedWidths = {...widths};
  
  // When a column is hidden, distribute its width to other columns
  if (groupBy === 'topic') {
    const distributedWidth = adjustedWidths.topic / 4;
    adjustedWidths.recommendation += distributedWidth;
    adjustedWidths.details += distributedWidth;
    adjustedWidths.organization += distributedWidth;
    adjustedWidths.assignee += distributedWidth;
    adjustedWidths.topic = 0;
  } else if (groupBy === 'organization') {
    const distributedWidth = adjustedWidths.organization / 4;
    adjustedWidths.recommendation += distributedWidth;
    adjustedWidths.details += distributedWidth;
    adjustedWidths.topic += distributedWidth;
    adjustedWidths.assignee += distributedWidth;
    adjustedWidths.organization = 0;
  } else if (groupBy === 'assignee') {
    const distributedWidth = adjustedWidths.assignee / 4;
    adjustedWidths.recommendation += distributedWidth;
    adjustedWidths.details += distributedWidth;
    adjustedWidths.topic += distributedWidth;
    adjustedWidths.organization += distributedWidth;
    adjustedWidths.assignee = 0;
  } else if (groupBy === 'similarRecommendations') {
    // Still need to handle this for grouping, but we won't show it as a column
    const distributedWidth = 10 / 4; // Use the original value
    adjustedWidths.recommendation += distributedWidth;
    adjustedWidths.details += distributedWidth;
    adjustedWidths.topic += distributedWidth;
    adjustedWidths.organization += distributedWidth;
  }
  
  return adjustedWidths;
};

// Define default column widths - keep original widths but in new order
const defaultColumnWidths = {
  recommendation: 15, // Original recommendation width
  organization: 15,   // Original organization width
  details: 45,        // Increased width for details since we're removing similarRecommendations
  topic: 12,          // Original topic width
  assignee: 13        // Slightly increased width for assignee
};

// Add a helper function to format text with bullet points
const formatBulletPoints = (text) => {
  if (!text) return '';
  
  // First, preprocess the text to normalize bullet points that appear in the middle of lines
  // This handles cases like "text ● bullet point" by converting to "text\n● bullet point"
  const preprocessed = text.replace(/(\S)\s+([-*•●◦○▪▫■□⦿⦾⁃⁌⁍‣⧫⬧⬨])\s+/g, '$1\n$2 ');
  
  // Split by newlines and check for bullet points
  const lines = preprocessed.split('\n');
  let inList = false;
  let formattedText = '';
  
  lines.forEach((line, index) => {
    // Enhanced regex to match more bullet point patterns:
    // 1. Common bullet characters: -, *, •, ●, ◦, ○, ▪, ▫, ■, □, ⦿, ⦾, ⁃, ⁌, ⁍, ‣, ⧫, ⬧, ⬨
    // 2. Lines that start with whitespace followed by these markers
    // 3. Numbered lists (1., 2., etc.)
    // 4. Letters with period (a., b., etc.)
    // 5. Bullet points without space after
    const bulletChars = '\\-\\*•●◦○▪▫■□⦿⦾⁃⁌⁍‣⧫⬧⬨';
    const isBullet = line.trim().match(new RegExp(`^[${bulletChars}](\\s|[A-Za-z0-9])`)) || 
                     line.match(new RegExp(`^\\s+[${bulletChars}](\\s|[A-Za-z0-9])`)) ||
                     line.trim().match(/^(\d+|[a-z])\.\s/i);
    
    if (isBullet) {
      if (!inList) {
        // Start a new list and add extra margin before if not at beginning
        inList = true;
        formattedText += '<ul style="margin: 6px 0 3px 0; padding-left: 16px; list-style-type: disc;">';
      }
      
      // Add list item, with improved handling of the bullet point marker
      // Handle numbered lists, letter lists, and standard bullets
      let content;
      if (line.trim().match(/^(\d+|[a-z])\.\s/i)) {
        // For numbered/letter lists, keep the number/letter as part of the content
        content = line;
      } else {
        // For standard bullets, remove the bullet marker but keep whitespace
        content = line.replace(new RegExp(`^(\\s*)[${bulletChars}](\\s|)`, 'u'), '$1');
      }
      
      formattedText += `<li style="margin-bottom: 3px; padding-top: 2px; padding-bottom: 2px; display: list-item; line-height: 1.3; text-indent: -4px; padding-left: 4px;">${content}</li>`;
    } else {
      if (inList) {
        // Close the list and add extra space after
        inList = false;
        formattedText += '</ul>';
      }
      
      if (line.trim()) {
        // For non-bullet lines, preserve the original line with consistent spacing
        formattedText += `<div style="margin-bottom: 6px;">${line}</div>`;
      } else if (index > 0 && index < lines.length - 1) {
        // Add paragraph break for empty lines, except at beginning or end
        formattedText += `<div style="margin-bottom: 12px;"></div>`;
      }
    }
  });
  
  // Close any open list
  if (inList) {
    formattedText += '</ul>';
  }
  
  return formattedText;
};

// Function to count bullet points in text
const countBulletPoints = (text) => {
  if (!text) return 0;
  const lines = text.split('\n');
  const bulletChars = '\\-\\*•●◦○▪▫■□⦿⦾⁃⁌⁍‣⧫⬧⬨';
  return lines.filter(line => 
    line.trim().match(new RegExp(`^[${bulletChars}](\\s|[A-Za-z0-9])`)) || 
    line.match(new RegExp(`^\\s+[${bulletChars}](\\s|[A-Za-z0-9])`)) ||
    line.trim().match(/^(\d+|[a-z])\.\s/i)
  ).length;
};

// Add this new function to help determine if text contains bullet points
const containsBulletPoints = (text) => {
  if (!text) return false;
  
  // Check for bullet point markers matching the same patterns as in formatBulletPoints
  const lines = text.split('\n');
  const bulletChars = '\\-\\*•●◦○▪▫■□⦿⦾⁃⁌⁍‣⧫⬧⬨';
  return lines.some(line => 
    line.trim().match(new RegExp(`^[${bulletChars}](\\s|[A-Za-z0-9])`)) || 
    line.match(new RegExp(`^\\s+[${bulletChars}](\\s|[A-Za-z0-9])`)) ||
    line.trim().match(/^(\d+|[a-z])\.\s/i)
  );
};

// Create a reusable tag container component
const SmartTagContainer = ({ 
  tags = [], 
  getColor, 
  maxHeight = 88,
  containerWidth,
  maxTags = null,
  isSmallScreen = false,
  adjustForBullets = false,
  onTagClick = null,
  tagType = null
}) => {
  const tagRef = useRef(null);
  const containerRef = useRef(null);
  
  // Process tags to ensure they're split properly
  const processedTags = useMemo(() => {
    // Handle null/undefined/empty cases
    if (!tags) return [];
    
    // Convert to array if not already
    const tagsArray = Array.isArray(tags) ? tags : [tags];
    
    // Don't split organization tags as they may contain commas in organization names
    if (tagType === 'organization') {
      return tagsArray.map(tag => String(tag).trim()).filter(Boolean);
    }
    
    // For other tag types, process each tag
    return tagsArray.reduce((acc, tag) => {
      if (!tag) return acc;
      
      const stringTag = String(tag).trim();
      if (!stringTag) return acc;
      
      // Split on commas if the tag contains them (except for organizations)
      if (stringTag.includes(',')) {
        return [...acc, ...stringTag.split(',').map(t => t.trim()).filter(Boolean)];
      }
      
      return [...acc, stringTag];
    }, []);
  }, [tags, tagType]);
  
  // Show all tags now instead of limiting them
  const displayedTags = processedTags;
  
  // Only render if we have tags
  if (!displayedTags.length) return null;
  
  // Handle tag click with event stopping to prevent row click
  const handleTagClick = (tag, event) => {
    if (event) {
      event.stopPropagation(); // Stop event from bubbling up to parent row
    }
    if (onTagClick) {
      onTagClick(tag, tagType);
    }
  };
  
  return (
    <div 
      ref={containerRef}
      style={{ 
        display: 'flex',
        flexWrap: 'wrap',
        gap: '4px',
        width: '100%',
        overflow: 'visible'
      }}
      onClick={(e) => e.stopPropagation()} // Prevent clicks on container from triggering row click
    >
      {displayedTags.map((tag, index) => (
        <div key={`${tag}-${index}`} style={{ 
          display: 'inline-block',
          margin: '0 0 4px 0'
        }}>
          <Tag 
            ref={index === 0 ? tagRef : null}
            $bgColor={getColor(tag).bg} 
            $textColor={getColor(tag).text}
            $nowrap={tagType === 'assignee' ? false : (tagType !== 'topic' && (containerWidth < 15 || isSmallScreen))} // Force assignee tags to use normal wrapping
            tagType={tagType} // Pass the tagType prop
            onClick={onTagClick ? (e) => handleTagClick(tag, e) : null} // Only add click handler if onTagClick is provided
          >
            {tag}
          </Tag>
        </div>
      ))}
    </div>
  );
};

// Helper function to process tag lists
const processTagList = (value, tagType = null) => {
  if (!value) return [];
  if (Array.isArray(value)) {
    const tags = value.map(String);
    // Filter out "unknown" assignees if tagType is "assignee"
    return tagType === 'assignee' ? tags.filter(tag => tag.toLowerCase() !== 'unknown') : tags;
  }
  if (typeof value !== 'string') {
    const tag = String(value);
    // Filter out "unknown" assignees if tagType is "assignee"
    return tagType === 'assignee' && tag.toLowerCase() === 'unknown' ? [] : [tag];
  }
  
  // Don't split organization field - treat as a single string since organization names may contain commas
  if (tagType === 'organization') {
    return [value.trim()];
  }

  // Split similar recommendations on semicolons
  if (tagType === 'similarRecommendations') {
    return value.split(';')
      .map(item => item.trim())
      .filter(item => item.length > 0);
  }
  
  // For other types, split on commas
  const tags = value.split(',')
    .map(item => item.trim())
    .filter(item => item.length > 0);
  // Filter out "unknown" assignees if tagType is "assignee"
  return tagType === 'assignee' ? tags.filter(tag => tag.toLowerCase() !== 'unknown') : tags;
};


const AIActionPlanDatabase = () => {
  // Add global styles for bullet points in collapsible text
  useEffect(() => {
    // Add a style tag for custom CSS
    const styleTag = document.createElement('style');
    styleTag.innerHTML = `
      .collapsible-text-content ul {
        padding-left: 16px !important;
        margin: 3px 0 !important;
      }
      .collapsible-text-content li {
        margin-bottom: 1px !important;
        padding-top: 1px !important;
        padding-bottom: 1px !important;
        line-height: 1.3 !important;
        text-indent: -4px !important;
        padding-left: 4px !important;
      }
      .truncated-text-container {
        position: relative;
        overflow: hidden;
        text-overflow: ellipsis;
      }
      .truncated-text-container ul {
        padding-left: 16px !important;
        margin: 3px 0 !important;
      }
      .truncated-text-container li {
        margin-bottom: 1px !important;
        padding-top: 1px !important;
        padding-bottom: 1px !important;
        line-height: 1.3 !important;
        text-indent: -4px !important;
        padding-left: 4px !important;
      }
      .recommendation-text {
        font-weight: 500;
        color: #1f2937;
      }
      .details-text {
        color: #6b7280;
      }
    `;
    document.head.appendChild(styleTag);
    
    // Clean up on unmount
    return () => {
      document.head.removeChild(styleTag);
    };
  }, []);
  
  // Get data from context instead of fetching directly
  const { data, loading, error, loadedCount, totalCount } = useData();
  
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState({});
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [isFilterMenuOpen, setIsFilterMenuOpen] = useState(false);
  const [groupBy, setGroupBy] = useState(null);
  const [collapsedGroups, setCollapsedGroups] = useState({});
  // Track which groups have had their content loaded
  const [loadedGroups, setLoadedGroups] = useState({});
  // Track which groups are currently loading their content
  const [loadingGroups, setLoadingGroups] = useState({});
  
  // Add state for filter modal and accordions
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);

  // Add state for responsive design
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [isSmallScreen, setIsSmallScreen] = useState(window.innerWidth < 768);
  
  // Keep page tracking but repurpose for infinite scroll
  const [currentPage, setCurrentPage] = useState(1);
  const batchSize = 100; // Number of items to load at once
  
  // Add state to track if we're loading more items
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMoreItems, setHasMoreItems] = useState(true);
  const loadMoreRef = useRef(null);
  
  const filterMenuRef = useRef(null);
  const filterButtonRef = useRef(null);
  const detailPanelRef = useRef(null);
  
  // Add new ref for the filter dropdown
  const filterModalRef = useRef(null);
  
  // Add new states for multiselect dropdowns
  const [isOrgDropdownOpen, setIsOrgDropdownOpen] = useState(false);
  const [orgSearchQuery, setOrgSearchQuery] = useState('');
  const [selectedOrgs, setSelectedOrgs] = useState([]);
  const orgDropdownRef = useRef(null);
  
  // --- NEW: dropdown horizontal alignment flags ---
  const [assigneeAlignLeft, setAssigneeAlignLeft] = useState(false);
  const [topicAlignLeft, setTopicAlignLeft] = useState(false);
  const [orgAlignLeft, setOrgAlignLeft] = useState(false);
  
  const [isAssigneeDropdownOpen, setIsAssigneeDropdownOpen] = useState(false);
  const [assigneeSearchQuery, setAssigneeSearchQuery] = useState('');
  const [selectedAssignees, setSelectedAssignees] = useState([]);
  const assigneeDropdownRef = useRef(null);
  
  const [isTopicDropdownOpen, setIsTopicDropdownOpen] = useState(false);
  const [topicSearchQuery, setTopicSearchQuery] = useState('');
  const [selectedCategories, setSelectedCategories] = useState([]);
  const topicDropdownRef = useRef(null);
  
  const [isGroupMenuOpen, setIsGroupMenuOpen] = useState(false);
  const groupMenuRef = useRef(null);
  const groupButtonRef = useRef(null);
  
  // Add a new state for mobile view
  const [isMobileView, setIsMobileView] = useState(window.innerWidth < 768);

  // Add state for similar recommendations
  const [selectedSimilarRecommendations, setSelectedSimilarRecommendations] = useState([]);
  const [similarRecSearchQuery, setSimilarRecSearchQuery] = useState('');

  // Add a ref for the similar recommendations dropdown
  const similarRecDropdownRef = useRef(null);
  const [isSimilarRecDropdownOpen, setIsSimilarRecDropdownOpen] = useState(false);
  const [similarRecAlignLeft, setSimilarRecAlignLeft] = useState(false);

  // Update mobile view state on resize
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
      setIsSmallScreen(window.innerWidth < 768);
      setIsMobileView(window.innerWidth < 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);
  
  // Add useEffect hooks to reset pagination when data changes
  useEffect(() => {
    // Reset to page 1 when filters change
    setCurrentPage(1);
  }, [filters]);
  
  useEffect(() => {
    // Reset to page 1 when grouping changes
    setCurrentPage(1);
  }, [groupBy]);
  
  useEffect(() => {
    // Reset to page 1 when sorting changes
    setCurrentPage(1);
  }, [sortConfig]);
  
  useEffect(() => {
    // Reset to page 1 when search query changes
    setCurrentPage(1);
  }, [searchQuery]);
  
  
  // Close filter menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Close filter menu when clicking outside
      if (
        isFilterMenuOpen && 
        filterMenuRef.current && 
        !filterMenuRef.current.contains(event.target) &&
        !filterButtonRef.current.contains(event.target)
      ) {
        setIsFilterMenuOpen(false);
      }
      
      // Close detail panel when clicking outside, but not when clicking on another row
      if (
        selectedRecord &&
        detailPanelRef.current && 
        !detailPanelRef.current.contains(event.target)
      ) {
        // Check if the click target is part of a row
        const isClickOnRow = event.target.closest('.item-row') !== null;
        
        // Only close the panel if not clicking on another row
        if (!isClickOnRow) {
          setSelectedRecord(null);
        }
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isFilterMenuOpen, selectedRecord]);

  // Add click outside handler for the organization dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        orgDropdownRef.current && 
        !orgDropdownRef.current.contains(event.target)
      ) {
        setIsOrgDropdownOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Add click outside handler for the group dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        isGroupMenuOpen &&
        groupMenuRef.current && 
        !groupMenuRef.current.contains(event.target) &&
        !groupButtonRef.current.contains(event.target)
      ) {
        setIsGroupMenuOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isGroupMenuOpen]);

  // Add click outside handlers for assignee and topic dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        isAssigneeDropdownOpen &&
        assigneeDropdownRef.current && 
        !assigneeDropdownRef.current.contains(event.target)
      ) {
        setIsAssigneeDropdownOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isAssigneeDropdownOpen]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        isTopicDropdownOpen &&
        topicDropdownRef.current && 
        !topicDropdownRef.current.contains(event.target)
      ) {
        setIsTopicDropdownOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isTopicDropdownOpen]);

  // Add window resize handling
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
      setIsSmallScreen(window.innerWidth < 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // --- NEW: Recalc dropdown alignment when open or window resizes ---
  useEffect(() => {
    const computeAlignment = () => {
      const margin = 16; // min distance from viewport edge
      const evaluate = (buttonRef, setAlignLeft) => {
        if (!buttonRef?.current) return;
        const rect = buttonRef.current.getBoundingClientRect();
        const menuWidth = 300; // matches dropdown width
        const viewportWidth = window.innerWidth;
        const spaceRight = viewportWidth - rect.right;
        const spaceLeft = rect.left;
        // Decide alignment: if we have enough room on the right, open to the right (align left edges).
        // Otherwise open to the left (align right edges).
        const openToRight = spaceRight >= menuWidth + margin || spaceRight >= spaceLeft;
        setAlignLeft(openToRight);
      };
      evaluate(assigneeDropdownRef, setAssigneeAlignLeft);
      evaluate(topicDropdownRef, setTopicAlignLeft);
      evaluate(orgDropdownRef, setOrgAlignLeft);
    };

    computeAlignment();
    window.addEventListener('resize', computeAlignment);
    return () => window.removeEventListener('resize', computeAlignment);
  }, [isAssigneeDropdownOpen, isTopicDropdownOpen, isOrgDropdownOpen]);
  
  // Sort the data
  const sortedData = useMemo(() => {
    if (!data) return [];
    return [...data].sort((a, b) => {
      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];
      
      if (aValue === bValue) return 0;
      
      const direction = sortConfig.direction === 'asc' ? 1 : -1;
      
      // Handle string comparisons
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return aValue.localeCompare(bValue) * direction;
      }
      
      // Handle undefined values
      if (aValue === undefined) return direction;
      if (bValue === undefined) return -direction;
      
      // Handle generic comparison
      return (aValue > bValue ? 1 : -1) * direction;
    });
  }, [data, sortConfig]);
    
  // Filter the data
  const filteredData = useMemo(() => {
    return sortedData.filter(item => {
      // Apply search query filter if it exists
      if (searchQuery && !Object.values(item).some(
        val => val && val.toString().toLowerCase().includes(searchQuery.toLowerCase())
      )) {
        return false;
      }
      
      // Apply dropdown filters
      if (filters.assignee) {
        // If item has no assignee, exclude it from results
        if (!item.assignee) return false;
        
        // Convert item.assignee to an array of assignees
        const itemAssignees = Array.isArray(item.assignee) 
          ? item.assignee.map(a => a.trim())
          : typeof item.assignee === 'string'
            ? item.assignee.split(',').map(a => a.trim()).filter(a => a.length > 0)
            : [String(item.assignee)];
        
        // Check if any of the selected assignees are in the item's assignees
        const assigneeMatch = Array.isArray(filters.assignee)
          ? filters.assignee.some(selected => itemAssignees.includes(selected))
          : itemAssignees.includes(filters.assignee);
          
        if (!assigneeMatch) return false;
      }
      
      // Apply topic filter
      if (filters.topic && filters.topic.length > 0) {
        // Get item topics as array
        const itemTopics = Array.isArray(item.topic) 
          ? item.topic 
          : typeof item.topic === 'string'
            ? item.topic.split(',').map(t => t.trim()).filter(t => t.length > 0)
            : [String(item.topic)];
        
        // Check if any selected topic matches
        const topicMatch = Array.isArray(filters.topic)
          ? filters.topic.some(selected => itemTopics.includes(selected))
          : itemTopics.includes(filters.topic);
          
        if (!topicMatch) return false;
      }

      // Apply similar recommendations filter
      if (filters.similarRecommendations && filters.similarRecommendations.length > 0) {
        // If item has no similar recommendations, exclude it from results
        if (!item.similarRecommendations) return false;
        
        // Get item similar recommendations as array
        const itemSimilarRecs = Array.isArray(item.similarRecommendations)
          ? item.similarRecommendations
          : typeof item.similarRecommendations === 'string'
            ? item.similarRecommendations.split(';').map(r => r.trim()).filter(r => r.length > 0)
            : [String(item.similarRecommendations)];
        
        // Check if any of the selected similar recommendations match
        const similarRecsMatch = Array.isArray(filters.similarRecommendations)
          ? filters.similarRecommendations.some(selected => 
              itemSimilarRecs.some(rec => rec.trim() === selected.trim())
            )
          : itemSimilarRecs.some(rec => rec.trim() === filters.similarRecommendations.trim());
        
        if (!similarRecsMatch) return false;
      }
      
      // Apply organization filter
      if (filters.organization && item.organization) {
        // Don't split organization field - treat as a single string since organization names may contain commas
        const itemOrganization = typeof item.organization === 'string' 
          ? item.organization.trim() 
          : String(item.organization || '');
        
        const organizationMatch = Array.isArray(filters.organization)
          ? filters.organization.includes(itemOrganization)
          : itemOrganization === filters.organization;
          
        if (!organizationMatch) return false;
      }
      
      // Apply orgType filter
      if (filters.orgType && item.orgType) {
        const itemOrgType = String(item.orgType).trim();
        const orgTypeMatch = Array.isArray(filters.orgType)
          ? filters.orgType.includes(itemOrgType)
          : itemOrgType === filters.orgType;
        
        if (!orgTypeMatch) return false;
      }
      
      return true;
    });
  }, [sortedData, searchQuery, filters]);
  
  // Add an effect to log filtered results
  useEffect(() => {
    console.log('9. Final filtered data length:', filteredData.length);
  }, [filteredData.length]);
  
  // Group the data if a groupBy is selected
  const groupedData = useMemo(() => {
    if (!groupBy) return {};
    
    const grouped = {};
    
    // Group items based on their field values
    filteredData.forEach(item => {
      if (groupBy === 'assigneeParent') {
        // Handle assignee parent grouping using governmentHierarchy
        const assignees = Array.isArray(item.assignee) 
          ? item.assignee 
          : typeof item.assignee === 'string'
            ? item.assignee.split(',').map(a => a.trim()).filter(a => a.length > 0)
            : [String(item.assignee || '')];

        // Find the parent department for each assignee
        assignees.forEach(assignee => {
          const parent = governmentHierarchy[assignee] || 'Uncategorized';
          if (!grouped[parent]) {
            grouped[parent] = [];
          }
          // Avoid duplicates
          const isDuplicate = grouped[parent].some(existingItem => existingItem.id === item.id);
          if (!isDuplicate) {
            grouped[parent].push({ ...item, originalData: { ...item } });
          }
        });
      } else if (item[groupBy]) {
        // Handle other grouping types as before
        let values = [];
        
        if (groupBy === 'topic' && Array.isArray(item.topic)) {
          values = item.topic;
        } else if (groupBy === 'similarRecommendations' && Array.isArray(item.similarRecommendations)) {
          values = item.similarRecommendations;
        } else if (typeof item[groupBy] === 'string') {
          if (groupBy === 'organization') {
            values = [item[groupBy].trim()];
          } else if (groupBy === 'similarRecommendations') {
            values = item[groupBy].split(';').map(val => val.trim()).filter(Boolean);
          } else {
            values = item[groupBy].split(',').map(val => val.trim()).filter(Boolean);
          }
        } else if (Array.isArray(item[groupBy])) {
          values = item[groupBy];
        } else {
          values = [String(item[groupBy])];
        }
        
        // If we're filtering by the same field we're grouping by, only include matching values
        let filteredValues = values;
        
        if (filters[groupBy]) {
          filteredValues = values.filter(val => 
            Array.isArray(filters[groupBy]) 
              ? filters[groupBy].includes(val) 
              : filters[groupBy] === val
          );
        }

        // Special handling for similar recommendations when topic filter is active
        if (groupBy === 'similarRecommendations' && filters.topic && filters.topic.length > 0) {
          // Get all recommendations that belong to the selected topics
          const topicRecommendations = new Set();
          (Array.isArray(filters.topic) ? filters.topic : [filters.topic]).forEach(topic => {
            if (topicHierarchy[topic]) {
              topicHierarchy[topic].forEach(rec => topicRecommendations.add(rec.trim()));
            }
          });

          // Only include similar recommendation groups that contain recommendations from the selected topics
          filteredValues = filteredValues.filter(value => topicRecommendations.has(value.trim()));
        }
        
        // Only proceed if we have values after filtering
        if (filteredValues.length > 0) {
          filteredValues.forEach(value => {
            if (!grouped[value]) {
              grouped[value] = [];
            }
            
            // Avoid duplicates
            const isDuplicate = grouped[value].some(existingItem => existingItem.id === item.id);
            
            if (!isDuplicate) {
              // Create a clone with specific value highlighted
              const clonedItem = { ...item, originalData: { ...item } };
              grouped[value].push(clonedItem);
            }
          });
        }
      } else if (!filters[groupBy] && groupBy !== 'similarRecommendations') {
        // Handle items with no value for the groupBy field, except for similarRecommendations
        const value = 'Unassigned';
        if (!grouped[value]) {
          grouped[value] = [];
        }
        const clonedItem = { ...item, originalData: { ...item } };
        grouped[value].push(clonedItem);
      }
    });
    
    return grouped;
  }, [filteredData, groupBy, filters]);
  
  // Create paginated groups with infinite scrolling support
  const displayedGroups = useMemo(() => {
    if (!groupBy) return {};
    
    // Use the already computed grouped data from groupedData (already contains all filtered items)
    const fullGroups = groupedData;

    // Create array of [groupName, items] pairs and sort by item count
    const sortedGroupPairs = Object.entries(fullGroups)
      .sort(([, itemsA], [, itemsB]) => {
        // Sort by number of items in descending order
        return itemsB.length - itemsA.length;
      });
    
    // Convert sorted pairs back to object
    const result = {};
    sortedGroupPairs.forEach(([groupName, items]) => {
      result[groupName] = items;
    });
    
    return result;
  }, [groupedData, groupBy]);
  
  // Function to load more items - defined with useCallback to avoid recreating it on every render
  const loadMoreItems = useCallback(() => {
    // Don't load more if we're loading, have no more items, or are in grouped view
    if (isLoadingMore || !hasMoreItems || groupBy) return;
    
    setIsLoadingMore(true);
    
    // Determine if there are more items to load
    const totalItems = filteredData.length;
    const loadedItems = currentPage * batchSize;
    
    // For ungrouped data, check if we need to load more
    if (loadedItems < totalItems) {
      // Small delay to prevent rapid loading and give visual feedback
      setTimeout(() => {
        setCurrentPage(prevPage => {
          return prevPage + 1;
        });
        setIsLoadingMore(false);
      }, 300);
    } else {
      setHasMoreItems(false);
      setIsLoadingMore(false);
    }
  }, [currentPage, batchSize, filteredData.length, hasMoreItems, isLoadingMore, groupBy]);

  // Add intersection observer for infinite scrolling
  useEffect(() => {
    const handleIntersection = (entries) => {
      const entry = entries[0];
      if (entry.isIntersecting && !isLoadingMore && hasMoreItems && !groupBy) {
        loadMoreItems();
      }
    };

    const observer = new IntersectionObserver(handleIntersection, {
      rootMargin: '200px', // Load more when user is 200px from bottom
      threshold: 0.1
    });

    const currentLoadMoreRef = loadMoreRef.current;
    if (currentLoadMoreRef) {
      observer.observe(currentLoadMoreRef);
    }

    return () => {
      if (currentLoadMoreRef) {
        observer.unobserve(currentLoadMoreRef);
      }
    };
  }, [isLoadingMore, hasMoreItems, loadMoreItems, groupBy]);

  // Use an effect to check loadable items whenever currentPage or filteredData changes
  useEffect(() => {
    const totalItems = filteredData.length;
    const loadedItems = currentPage * batchSize;
    
    // For grouped data, we now show all items, so there are no more to load
    if (groupBy) {
      setHasMoreItems(false);
    } else {
      // For ungrouped data, check if we've loaded all items
      const hasMore = loadedItems < totalItems;
      setHasMoreItems(hasMore);
    }
  }, [filteredData, currentPage, batchSize, groupBy]);

  // Effect to reset when filters change
  useEffect(() => {
    setCurrentPage(1);
    setHasMoreItems(true);
  }, [filters, searchQuery]); // Removed groupBy from dependencies
  
  // Get display data for infinite scrolling - modified to include all loaded items
  const displayedData = useMemo(() => {
    if (!groupBy) {
      // For ungrouped data, display all items up to current page
      const itemsToShow = currentPage * batchSize;
      return filteredData.slice(0, Math.min(itemsToShow, filteredData.length));
    }
    return [];
  }, [filteredData, currentPage, batchSize, groupBy]);
  
  // Reset collapsed groups when groupBy changes - set all groups to collapsed by default
  useEffect(() => {
    if (groupBy) {
      // Create an object with all groups collapsed by default
      const allGroups = {};
      
      // When groupBy changes, get all possible group values from the entire filteredData
      filteredData.forEach(item => {
        if (item[groupBy]) {
          // Extract all possible values from this item for the groupBy field
          let values = [];
          
          if (groupBy === 'topic' && Array.isArray(item.topic)) {
            values = item.topic;
          } else if (typeof item[groupBy] === 'string') {
            // Don't split organization field on commas
            if (groupBy === 'organization') {
              values = [item[groupBy].trim()];
            } else {
              values = item[groupBy].split(',').map(val => val.trim()).filter(Boolean);
            }
          } else if (Array.isArray(item[groupBy])) {
            values = item[groupBy];
          } else {
            values = [String(item[groupBy])];
          }
          
          // Set each value as a collapsed group
          values.forEach(value => {
            allGroups[value] = true; // true means collapsed
          });
        } else {
          // Handle unassigned items
          allGroups['Unassigned'] = true;
        }
      });
      
      setCollapsedGroups(allGroups);
      // Reset loaded groups when groupBy changes
      setLoadedGroups({});
    } else {
      // If groupBy is null, reset to empty object
      setCollapsedGroups({});
      setLoadedGroups({});
    }
  }, [groupBy, filteredData]);
  
  // Handle row click to show detail panel
  const handleRowClick = (record) => {
    // If we click on the same record again, toggle the panel
    if (selectedRecord && selectedRecord.id === record.id) {
      setSelectedRecord(null);
    } else {
      // Use originalData if available, otherwise use the record itself
      const finalRecord = record.originalData || record;
      setSelectedRecord(finalRecord);
    }
  };
  
  // Handle sort
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Handle toggling an organization in the multiselect
  const toggleOrganization = (org) => {
    setSelectedOrgs(prev => {
      const newSelection = prev.includes(org)
        ? prev.filter(o => o !== org)
        : [...prev, org];
      
      // Update the filters object
      setFilters(prevFilters => ({
        ...prevFilters,
        organization: newSelection.length > 0 ? newSelection : undefined
      }));
      
      return newSelection;
    });
  };
  
  // Handle toggling an assignee in the multiselect
  const toggleAssignee = (assignee) => {
    setSelectedAssignees(prev => {
      const newSelection = prev.includes(assignee)
        ? prev.filter(a => a !== assignee)
        : [...prev, assignee];
      
      // Update the filters object
      setFilters(prevFilters => ({
        ...prevFilters,
        assignee: newSelection.length > 0 ? newSelection : undefined
      }));
      
      return newSelection;
    });
  };
  
  // Handle toggling a topic filter
  const toggleTopicFilter = (topic) => {
    setSelectedCategories(prev => {
      const newSelection = prev.includes(topic)
        ? prev.filter(c => c !== topic)
        : [...prev, topic];
      
      // Update the filters object
      setFilters(prevFilters => ({
        ...prevFilters,
        topic: newSelection.length > 0 ? newSelection : undefined
      }));
      
      return newSelection;
    });
  };

  // Add a function to handle toggling a whole topic
  const toggleTopic = (topic) => {
    setSelectedCategories(prev => {
      const newSelection = prev.includes(topic)
        ? prev.filter(cat => cat !== topic)
        : [...prev, topic];
      
      // Update the filters object
      setFilters(prevFilters => ({
        ...prevFilters,
        topic: newSelection.length > 0 ? newSelection : undefined
      }));
      
      return newSelection;
    });
  };

  // Clear all selected organizations
  const clearOrganizations = () => {
    setSelectedOrgs([]);
    setSelectedOrgTypes([]);  // Also clear orgTypes when clearing organizations
    setFilters(prev => {
      const newFilters = { ...prev };
      delete newFilters.organization;
      delete newFilters.orgType;  // Also delete orgType from filters
      return newFilters;
    });
  };
  
  // Clear all selected assignees
  const clearAssignees = () => {
    setSelectedAssignees([]);
    setFilters(prev => {
      const newFilters = { ...prev };
      delete newFilters.assignee;
      return newFilters;
    });
  };
  
  // Clear all selected categories
  const clearCategories = () => {
    setSelectedCategories([]);
    setFilters(prev => {
      const newFilters = { ...prev };
      delete newFilters.topic;
      return newFilters;
    });
  };

  // Get unique values from the data for dropdowns
  const uniqueValues = useMemo(() => {
    const values = {
      assignee: new Set(),
      topic: new Set(),
      organization: new Set(),
      orgType: new Set(), // Add orgType to collect unique org types
    };
    
    // Extract all unique values from the data
    data.forEach(item => {
      // For categories, handle array properly
      if (item.topic) {
        if (Array.isArray(item.topic)) {
          // Add each topic from the array to the set
          item.topic.forEach(cat => {
            if (cat && typeof cat === 'string') {
              values.topic.add(cat.trim());
            } else if (cat) {
              values.topic.add(String(cat));
            }
          });
        } else if (typeof item.topic === 'string') {
          // Handle legacy string values
          if (item.topic.includes(',')) {
            // Split comma-separated string
            item.topic.split(',')
              .map(val => val.trim())
              .filter(val => val.length > 0)
              .forEach(val => values.topic.add(val));
          } else {
            // Add single value
            values.topic.add(item.topic.trim());
          }
        } else {
          // Handle other types
          values.topic.add(String(item.topic));
        }
      }
      
      if (item.organization) {
        // Don't split organization names - treat as a single value
        const orgValue = typeof item.organization === 'string' 
          ? item.organization.trim() 
          : String(item.organization).trim();
        values.organization.add(orgValue);
      }
      
      if (item.assignee) {
        // Check if assignee is a string before splitting
        if (typeof item.assignee === 'string') {
          item.assignee.split(',')
            .map(val => val.trim())
            .filter(val => val.length > 0)
            .forEach(val => values.assignee.add(val));
        } else {
          // Handle non-string values
          values.assignee.add(String(item.assignee));
        }
      }
      
      // Add orgType to the set if it exists
      if (item.orgType) {
        values.orgType.add(String(item.orgType).trim());
      }
    });
 
    // Convert sets to sorted arrays
    return {
      assignee: Array.from(values.assignee).sort(),
      topic: Array.from(values.topic).sort(),
      organization: Array.from(values.organization).sort(),
      orgType: Array.from(values.orgType).sort()
    };
  }, [data]);

  // Get tag colors based on type and value
  const getTagColors = (type, value) => {
    
    // Predefined color schemes for each type
    const colorSchemes = {
      topic: [
        { bg: '#8F2853', text: '#ffffff' }, // red
      ],
      organization: [
        { bg: '#1C51B7', text: '#ffffff' }, // blue
      ],
      assignee: [
        { bg: '#EFEBE0', text: '#000000' },  // cream
      ],
      orgType: [
        { bg: '#22A0D7', text: '#ffffff' },  // blue for orgType tags
      ],
      similarRecommendations: [
        { bg: '#f0f9ff', text: '#0369a1' }, // light blue
      ],
      assigneeParent: [
        { bg: '#8B4513', text: '#ffffff' }, // brown with white text
      ]
    };
    
    // Hard-coded specific values (for consistency with existing UI)
    const specificColors = {
      topic: {},
      organization: {},
      assignee: {},
      orgType: {},
      similarRecommendations: {},
      assigneeParent: {}
    };

    // Special case for similarRecommendations
    if (type === 'similarRecommendations') {
      return { 
        bg: '#f0f9ff', 
        text: '#0369a1',
        border: '#bae6fd'
      };
    }

    // Special case for assigneeParent (agency) type
    if (type === 'assigneeParent') {
      return {
        bg: '#8B4513',
        text: '#ffffff'
      };
    }

    // Return specific color if defined
    if (specificColors[type]?.[value]) {
      return specificColors[type][value];
    }
    
    // Otherwise, generate a consistent color based on the string
    if (!value) {
      return { bg: '#e5e7eb', text: '#374151' }; // Default for empty values
    }
    
    try {
      // Generate a simple hash from the string to use as index
      const hashCode = typeof value === 'string' 
        ? value.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0)
        : String(value).split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
      
      // Use the hash to select a color from the appropriate scheme
      const scheme = colorSchemes[type] || colorSchemes.topic;
      const colorIndex = hashCode % scheme.length;
      
      return scheme[colorIndex];
    } catch (error) {
      console.error('Error in getTagColors:', error);
      console.error('Problem value:', value, `typeof value=${typeof value}`);
      // Fallback to default color
      return { bg: '#e5e7eb', text: '#374151' };
    }
  };

  // Add function to toggle group collapse state
  const toggleGroupCollapse = (group) => {
    // Update collapsed state
    setCollapsedGroups(prev => {
      const newState = { ...prev };
      newState[group] = !prev[group];
      
      // If we're expanding the group and it hasn't been loaded yet
      if (!newState[group] && !loadedGroups[group]) {
        // Show loading state
        setLoadingGroups(prevLoading => ({
          ...prevLoading,
          [group]: true
        }));
        
        // Simulate a delay to show loading indicator
        // In a real implementation, this could be when you fetch additional data
        setTimeout(() => {
          // Mark the group as loaded
          setLoadedGroups(prevLoaded => ({
            ...prevLoaded,
            [group]: true
          }));
          
          // Remove loading state
          setLoadingGroups(prevLoading => ({
            ...prevLoading,
            [group]: false
          }));
        }, 300); // Small delay to simulate loading
      } else if (!newState[group]) {
        // If already loaded, just mark as loaded immediately
        setLoadedGroups(prevLoaded => ({
          ...prevLoaded,
          [group]: true
        }));
      }
      
      return newState;
    });
  };

  // Use columnWidths directly or recalculate when needed
  const adjustedWidths = useMemo(() => {
    return getAdjustedColumnWidths(defaultColumnWidths, windowWidth, groupBy);
  }, [windowWidth, groupBy]);

  // Add CollapsibleText component for detail panel
  const CollapsibleText = ({ children, maxLines = 8 }) => {
    const [expanded, setExpanded] = useState(false);
    const contentRef = useRef(null);
    const [needsToggle, setNeedsToggle] = useState(false);
    
    // Detect if content has bullet points to adjust maxLines
    const hasBullets = useMemo(() => {
      const div = document.createElement('div');
      div.innerHTML = children;
      const textContent = div.textContent || div.innerText;
      return containsBulletPoints(textContent);
    }, [children]);
    
    // Adjust maxLines upward if bullets are present to fit more
    const adjustedMaxLines = hasBullets ? Math.max(6, maxLines + 2) : maxLines;
    
    useEffect(() => {
      // Simpler detection if we need a toggle
      if (contentRef.current) {
        const element = contentRef.current;
        // Use a generous buffer to ensure we're not cutting off bullets
        const lineHeight = parseInt(window.getComputedStyle(element).lineHeight) || 24;
        const maxHeight = lineHeight * adjustedMaxLines;
        
        // Check if content is significantly taller than the max height
        // Using a 20px buffer to avoid edge cases with bullets
        setNeedsToggle(element.scrollHeight > maxHeight + 20);
      }
    }, [children, adjustedMaxLines]);
    
    return (
      <div>
        {/* When not expanded, add text-overflow: ellipsis */}
        <div
          className={`collapsible-content ${!expanded ? 'collapsed' : ''}`}
          style={{
            position: 'relative',
            maxHeight: expanded ? 'none' : `${adjustedMaxLines * 1.5}em`,
            overflow: expanded ? 'visible' : 'hidden',
            transition: 'max-height 0.3s ease',
            textOverflow: !expanded && needsToggle ? 'ellipsis' : 'clip'
          }}
        >
          {/* The actual content */}
          <div 
            ref={contentRef}
            dangerouslySetInnerHTML={{ __html: children }}
            style={{
              margin: 0,
              padding: 0,
              lineHeight: '1.3'
            }}
            className="collapsible-text-content"
          />
        </div>
        
        {needsToggle && (
          <div 
            onClick={() => setExpanded(!expanded)}
            style={{
              marginTop: '8px',
              color: '#ae7cef',
              fontWeight: 500,
              fontSize: '14px',
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              zIndex: 5,
              position: 'relative',
            }}
          >
            {expanded ? 'Read less' : 'Read more'}
            {expanded ? <ChevronUp size={16} style={{ marginLeft: '4px' }} /> : <ChevronDown size={16} style={{ marginLeft: '4px' }} />}
          </div>
        )}
      </div>
    );
  };

  // Add a TruncatedTextWithBullets component for handling text with controlled truncation
  const TruncatedTextWithBullets = ({ text, maxLines, className = '' }) => {
    // Determine if the text contains bullet points
    const hasBullets = useMemo(() => containsBulletPoints(text), [text]);
    
    // Format the text with bullet points
    const formattedText = useMemo(() => formatBulletPoints(text), [text]);
    
    // Adjust maxLines based on bullet points
    const effectiveMaxLines = hasBullets ? Math.max(3, maxLines) : maxLines;
    
    return (
      <div 
        className={`truncated-text-container ${className}`}
        style={{ 
          overflow: 'visible',
          display: '-webkit-box',
          WebkitLineClamp: effectiveMaxLines,
          WebkitBoxOrient: 'vertical',
          lineHeight: '1.3',
          position: 'relative',
          wordBreak: 'break-word',
          width: '100%',
          minHeight: hasBullets ? '60px' : 'auto',
          height: 'auto'
        }}
      >
        <div
          dangerouslySetInnerHTML={{ 
            __html: formattedText 
          }}
          style={{ 
            margin: 0,
            padding: 0
          }}
        />
      </div>
    );
  };

  const CardContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
    margin-top: 16px;
  `;

  const Card = styled.div`
    background: white;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    padding: 16px;
    display: flex;
    flex-direction: column;
    gap: 8px;
  `;

  const CardHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 12px;
  `;

  const CardTitle = styled.div`
    font-weight: 600;
    color: #1f2937;
    font-size: 16px;
    line-height: 1.4;
    flex: 1;
  `;

  const DetailsButton = styled.button`
    background: none;
    border: none;
    color: #373737;
    cursor: pointer;
    padding: 6px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    
    &:hover {
      color: #6b7280;
      background: #f9fafb;
    }
  `;

  const CardTags = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  `;

  // Effect to prevent background scrolling when detail panel is open
  useEffect(() => {
    if (selectedRecord) {
      // Allow scrolling on the underlying table
      document.body.style.overflow = 'auto';
    } else {
      document.body.style.overflow = 'auto';
    }
    
    // Cleanup function to restore scrolling if component unmounts
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [selectedRecord]);

  // Add hover effects to item rows
  const ItemRow = styled.div`
    position: relative;
    border-bottom: ${props => props.isLastItem ? 'none' : '1px solid #e5e7eb'};
    cursor: pointer;
    display: flex;
    padding: 12px 16px;
    /* Allow height to adjust dynamically based on content */
    min-height: ${props => props.hasBulletPoints ? '100px' : '90px'};
    height: auto;
    box-sizing: border-box;
    background-color: white;
    transition: background-color 0.2s ease;
    
    &:hover {
      background-color: #f9fafb;
    }
  `;

  // Add a new helper function to check if an entity is a department
  const isDepartment = (name) => {
    return Object.values(governmentHierarchy).includes(name) || Object.keys(getAssigneeHierarchy?.hierarchy || {}).includes(name);
  };

  // Update the tag filter handler to check for departments when clicking assignee tags
  const handleTagFilter = (tag, tagType) => {
    // Based on the tag type, update the appropriate filter
    if (tagType === 'topic') {
      // Check if this is a parent topic
      if (Object.keys(topicHierarchy).includes(tag)) {
        toggleTopic(tag);
      } else {
        toggleTopicFilter(tag);
      }
    } else if (tagType === 'assignee') {
      // Check if this assignee is actually a department
      if (isDepartment(tag)) {
        // If it's a department, toggle the whole department
        toggleDepartment(tag);
      } else {
        // Otherwise, toggle as a regular assignee
        toggleAssignee(tag);
      }
    } else if (tagType === 'organization') {
      toggleOrganization(tag);
    } else if (tagType === 'orgType') {
      toggleOrgType(tag, true); // Skip org selection when clicking tag
    } else if (tagType === 'similarRecommendations') {
      toggleSimilarRecommendation(tag);
    }
  };

  // Categories for quick filter buttons
  /*
  const filterButtonCategories = [
    "Export Controls",
    "Infrastructure",
    "Security",
    "Open Source",
    "Evidence of Risks"
  ];
  */

  // Use state to manage the organization type filter buttons
  /*
  const filterButtonOrgTypes = useMemo(() => [
    "Think Tank",
    "Trade Association",
    "Civil Society",
    "Frontier AI Developer",
    "Industry"
  ], []);
  */

  // Add a state for selected orgTypes
  const [selectedOrgTypes, setSelectedOrgTypes] = useState([]);

  // Handle toggling an orgType in the multiselect - simple direct filtering
  const toggleOrgType = (orgType) => {
    // Get all organizations under this org type
    const orgsUnderType = getOrgHierarchy?.hierarchy?.[orgType] || [];
      
    setSelectedOrgTypes(prev => {
      const newSelection = prev.includes(orgType)
        ? prev.filter(t => t !== orgType)
        : [...prev, orgType];
      
      // Update the filters object
      setFilters(prevFilters => {
        const newFilters = { ...prevFilters };
        
        if (newSelection.length > 0) {
          newFilters.orgType = newSelection;
        } else {
          delete newFilters.orgType;
        }
        
        return newFilters;
      });
      
      // If adding an org type, also select all its organizations
      if (!prev.includes(orgType)) {
        // Add all orgs under this type to selectedOrgs
        setSelectedOrgs(prevOrgs => {
          const newOrgs = [...prevOrgs];
          orgsUnderType.forEach(org => {
            if (!newOrgs.includes(org)) {
              newOrgs.push(org);
            }
          });
          
          // Update organization filters
          setFilters(prevFilters => ({
            ...prevFilters,
            organization: newOrgs.length > 0 ? newOrgs : undefined
          }));
          
          return newOrgs;
        });
      } else {
        // If removing an org type, also deselect all its organizations
        setSelectedOrgs(prevOrgs => {
          const newOrgs = prevOrgs.filter(org => !orgsUnderType.includes(org));
          
          // Update organization filters
          setFilters(prevFilters => ({
            ...prevFilters,
            organization: newOrgs.length > 0 ? newOrgs : undefined
          }));
          
          return newOrgs;
        });
      }
      
      return newSelection;
    });
  };
  

  // Add logging for orgType values to help debug
  useEffect(() => {
    if (data.length > 0) {
      
      // Log unique orgType values
      const uniqueOrgTypes = new Set();
      data.forEach(item => {
        if (item.orgType) {
          uniqueOrgTypes.add(String(item.orgType).trim());
        }
      });
    }
  }, [data]);

  const closeFilterModal = () => {
    setIsFilterModalOpen(false);
  };

  // HeaderCell styling with fixed padding
  const HeaderCell = styled.div`
    position: relative;
    font-size: 12px;
    font-weight: 500;
    color: #6b7280;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    padding: 0 16px;
    display: flex;
    align-items: center;
    cursor: pointer;
    user-select: none;
    
    /* Add font weight increase when sorted */
    &.active-sort {
      font-weight: 600;
      color: #4b5563;
    }
    
    .cell-content {
      display: flex;
      align-items: center;
      width: 100%;
      justify-content: space-between;
    }
    
    &:hover .sort-icon {
      opacity: 0.9;
    }
    
    .sort-icon {
      margin-left: 8px;
      opacity: 0.4;
      transition: opacity 0.2s;
      display: flex;
      flex-direction: column;
      height: 22px;
      width: 18px;
      position: relative;
      flex-shrink: 0;
    }
    
    .up-arrow, .down-arrow {
      position: absolute;
      height: 11px;
      width: 18px;
      display: flex;
      justify-content: center;
      align-items: center;
      transition: all 0.2s;
      background-color: transparent;
      border-radius: 2px;
    }
    
    .up-arrow {
      top: 0;
    }
    
    .down-arrow {
      bottom: 0;
    }
    
    .active-sort {
      opacity: 1;
    }
    
    .active-asc .up-arrow {
      opacity: 1;
      color: #1f2937; /* Much darker grey */
      transform: scale(1.2);
      background-color: transparent; /* Remove background color */
      box-shadow: none; /* Remove shadow too */
    }
    
    .active-asc .down-arrow {
      opacity: 0.25; /* Make inactive arrow more subtle */
    }
    
    .active-desc .down-arrow {
      opacity: 1;
      color: #1f2937; /* Much darker grey */
      transform: scale(1.2);
      background-color: transparent; /* Remove background color */
      box-shadow: none; /* Remove shadow too */
    }
    
    .active-desc .up-arrow {
      opacity: 0.25; /* Make inactive arrow more subtle */
    }
  `;

  // Add click outside handler for the organization dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        orgDropdownRef.current && 
        !orgDropdownRef.current.contains(event.target)
      ) {
        setIsOrgDropdownOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Add click outside handler for the group dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        isGroupMenuOpen &&
        groupMenuRef.current && 
        !groupMenuRef.current.contains(event.target) &&
        !groupButtonRef.current.contains(event.target)
      ) {
        setIsGroupMenuOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isGroupMenuOpen]);
  
  // Add click outside handler for the filter dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        isFilterModalOpen &&
        filterModalRef.current && 
        !filterModalRef.current.contains(event.target)
      ) {
        closeFilterModal();
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isFilterModalOpen]);

  // Filter data using the specified filters
  // This is unused/duplicate code - removing to fix the error
  
  const ResizableHeaderCell = ({ width, onResize, children, onClick, ...props }) => {
    const [isResizing, setIsResizing] = useState(false);
    
    const startResize = (e) => {
      setIsResizing(true);
      document.addEventListener('mousemove', resize);
      document.addEventListener('mouseup', stopResize);
      e.preventDefault();
      e.stopPropagation(); // Stop propagation to prevent triggering onClick
    };
    
    const resize = useCallback((e) => {
      if (isResizing) {
        onResize(e.clientX);
      }
    }, [isResizing, onResize]);
    
    const stopResize = useCallback(() => {
      setIsResizing(false);
      document.removeEventListener('mousemove', resize);
      document.removeEventListener('mouseup', stopResize);
    }, [resize]);
    
    // Handle click on the header cell itself (for sorting)
    const handleClick = (e) => {
      if (!isResizing && onClick) {
        onClick(e);
      }
    };
    
    return (
      <HeaderCell 
        style={{ width: `${width}%`, position: 'relative' }} 
        onClick={handleClick} 
        {...props}
      >
        {children}
        <div 
          style={{
            position: 'absolute',
            right: 0,
            top: 0,
            height: '100%',
            width: '5px',
            cursor: 'col-resize',
            zIndex: 10
          }}
          onMouseDown={startResize}
        />
      </HeaderCell>
    );
  };

  // Add state for tracking expanded org types
  const [expandedOrgTypes, setExpandedOrgTypes] = useState({});

  // Add this function to get the hierarchical organization data
  const getOrgHierarchy = useMemo(() => {
    const hierarchy = {};
    const orgToTypeMap = {};
    const multipleTypeOrgs = new Set();
    
    // First pass - build a map of organization to org type
    data.forEach(item => {
      if (item.organization && item.orgType) {
        // Don't split organization field - treat as a single string since organization names may contain commas
        const org = String(item.organization).trim();
        const orgType = String(item.orgType).trim();
        
        // Check if this org already has a different type assigned
        if (orgToTypeMap[org] && orgToTypeMap[org] !== orgType) {
          multipleTypeOrgs.add(org);
        }
        orgToTypeMap[org] = orgType;
      }
    });
    
    // Second pass - build the hierarchy
    uniqueValues.orgType.forEach(type => {
      hierarchy[type] = [];
    });
    
    // Add organizations to their types
    uniqueValues.organization.forEach(org => {
      const type = orgToTypeMap[org] || 'Uncategorized';
      
      // Initialize the type array if it doesn't exist
      if (!hierarchy[type]) {
        hierarchy[type] = [];
      }
      
      hierarchy[type].push(org);
    });
    
    // Sort organizations within each type
    Object.keys(hierarchy).forEach(type => {
      hierarchy[type].sort();
    });
    
    return {
      hierarchy,
      multipleTypeOrgs: Array.from(multipleTypeOrgs)
    };
  }, [data, uniqueValues.organization, uniqueValues.orgType]);

  // Add a function to toggle org type expansion
  const toggleOrgTypeExpansion = (orgType) => {
    setExpandedOrgTypes(prev => ({
      ...prev,
      [orgType]: !prev[orgType]
    }));
  };

  // Update the filtered organizations logic to include searching by org type
  const filteredOrgHierarchy = useMemo(() => {
    if (!getOrgHierarchy || !getOrgHierarchy.hierarchy) {
      return {};
    }
    
    const { hierarchy } = getOrgHierarchy;
    
    if (!orgSearchQuery) {
      // If no search query, return the full hierarchy
      return hierarchy;
    }
    
    const result = {};
    const searchLower = orgSearchQuery.toLowerCase();
    
    // Filter both org types and organizations
    Object.keys(hierarchy).forEach(type => {
      // Check if type matches search
      const typeMatches = type.toLowerCase().includes(searchLower);
      
      // Filter organizations in this type
      const matchingOrgs = hierarchy[type].filter(org => 
        org.toLowerCase().includes(searchLower)
      );
      
      // Include this type if either the type matches or it has matching orgs
      if (typeMatches || matchingOrgs.length > 0) {
        result[type] = typeMatches ? hierarchy[type] : matchingOrgs;
        
        // Auto-expand this type when there are search results
        if (matchingOrgs.length > 0) {
          setExpandedOrgTypes(prev => ({
            ...prev,
            [type]: true
          }));
        }
      }
    });
    
    return result;
  }, [getOrgHierarchy, orgSearchQuery]);

  // Add styled components for the org type dropdown
  const OrgTypeHeader = styled.div`
    display: flex;
    align-items: center;
    padding: 10px 16px;
    color: #4b5563;
    font-size: 13px;
    font-weight: 500;
    margin-bottom: 0;
    cursor: pointer;
    width: 100%;
    box-sizing: border-box;
    
    &:hover {
      background-color: #f9fafb;
    }
  `;

  const OrgTypeLabel = styled.div`
    display: flex;
    align-items: center;
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
  `;

  const OrgsContainer = styled.div`
    margin: 0 0 0 0;
    padding-left: 36px;
    padding-right: 16px;
  `;

  const OrgTypeCount = styled.span`
    font-size: 12px;
    color: #6b7280;
    margin-left: 8px;
    font-weight: normal;
  `;

  const OrgItem = styled.div`
    display: flex;
    align-items: center;
    padding: 6px 4px;
    border-radius: 4px;
    margin-bottom: 2px;
    
    &:hover {
      background-color: #f9fafb;
    }
    
    label {
      display: flex;
      align-items: center;
      justify-content: space-between;
      width: 100%;
      margin-left: 10px;
      font-size: 14px;
      cursor: pointer;
      color: #374151;
    }
    
    ${props => props.$hasMultipleTypes && `
      border-left: 2px solid #ef4444;
      padding-left: 8px;
    `}
  `;

  // Reset expanded types when closing the dropdown
  useEffect(() => {
    if (!isOrgDropdownOpen) {
      // Reset expanded states when closing the dropdown
      if (orgSearchQuery) {
        setOrgSearchQuery('');
      } else {
        setExpandedOrgTypes({});
      }
    }
  }, [isOrgDropdownOpen, orgSearchQuery]); // Added orgSearchQuery as dependency

  // Render the organization dropdown
  const orgDropdownContent = isOrgDropdownOpen && (
    <FilterDropdownMenu $alignLeft={orgAlignLeft}>
      <FilterItemsContainer>
        <FilterSearchInput 
          type="text"
          placeholder="Search organizations"
          value={orgSearchQuery}
          onChange={(e) => setOrgSearchQuery(e.target.value)}
        />
        
        {Object.keys(filteredOrgHierarchy).length === 0 ? (
          <div style={{ color: '#6b7280', fontSize: '14px', padding: '4px 16px' }}>
            No organizations found
          </div>
        ) : (
          Object.keys(filteredOrgHierarchy).sort().map(orgType => {
            const orgs = filteredOrgHierarchy[orgType];
            const isOrgTypeSelected = selectedOrgTypes.includes(orgType);
            const selectedOrgsCount = orgs.filter(org => selectedOrgs.includes(org)).length;
            const isExpanded = expandedOrgTypes[orgType] || orgSearchQuery || false;
            // Add this line to calculate partial selection
            const isPartiallySelected = selectedOrgsCount > 0 && selectedOrgsCount < orgs.length;
            
            return (
              <div key={orgType}>
                <OrgTypeHeader onClick={() => toggleOrgTypeExpansion(orgType)}>
                  <OrgTypeLabel>
                    <input 
                      type="checkbox" 
                      id={`dropdown-orgtype-${orgType}`}
                      checked={isOrgTypeSelected}
                      ref={el => {
                        // Handle indeterminate state for "partially" selected org types
                        if (el) {
                          el.indeterminate = isPartiallySelected;
                        }
                      }}
                      onChange={(e) => {
                        e.stopPropagation();
                        toggleOrgType(orgType);
                      }}
                      onClick={(e) => e.stopPropagation()}
                      style={{ marginRight: '10px' }}
                    />
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {orgType}
                      <OrgTypeCount>({selectedOrgsCount}/{orgs.length})</OrgTypeCount>
                    </span>
                  </OrgTypeLabel>
                  
                  <div style={{ marginLeft: '8px' }}>
                    {isExpanded ? (
                      <ChevronUp size={16} />
                    ) : (
                      <ChevronDown size={16} />
                    )}
                  </div>
                </OrgTypeHeader>
                
                {isExpanded && (
                  <OrgsContainer>
                    {orgs.map(org => {
                      // Check if this org has multiple types (error case)
                      const hasMultipleTypes = getOrgHierarchy?.multipleTypeOrgs?.includes(org) || false;
                      const isOrgSelected = selectedOrgs.includes(org);
                      
                      return (
                        <OrgItem 
                          key={org}
                          $hasMultipleTypes={hasMultipleTypes}
                          $selected={isOrgSelected}
                        >
                          <input 
                            type="checkbox" 
                            id={`dropdown-org-${org}`}
                            checked={isOrgSelected}
                            onChange={() => toggleOrganization(org)}
                          />
                          <label htmlFor={`dropdown-org-${org}`}>
                            <span>{org}</span>
                            {hasMultipleTypes && (
                              <span 
                                style={{ 
                                  fontSize: '11px', 
                                  color: '#b91c1c',
                                  marginLeft: '4px'
                                }}
                                title="This organization has multiple org types"
                              >
                                *
                              </span>
                            )}
                          </label>
                        </OrgItem>
                      );
                    })}
                  </OrgsContainer>
                )}
              </div>
            );
          })
        )}
      </FilterItemsContainer>
    </FilterDropdownMenu>
  );

  // Add state for tracking expanded departments (similar to expandedOrgTypes)
  const [expandedDepartments, setExpandedDepartments] = useState({});
  
  // Add this function to generate the assignee hierarchy
  const getAssigneeHierarchy = useMemo(() => {
    const hierarchy = {};
    const unknownDepartment = "Uncategorized";
    
    // Initialize all known departments from the governmentHierarchy
    const departments = new Set(Object.values(governmentHierarchy));
    departments.forEach(dept => {
      hierarchy[dept] = [];
    });
    
    // Add an "Uncategorized" department
    hierarchy[unknownDepartment] = [];
    
    // Populate the hierarchy with assignees
    uniqueValues.assignee.forEach(assignee => {
      const department = governmentHierarchy[assignee] || unknownDepartment;
      
      // Initialize the department array if it doesn't exist
      if (!hierarchy[department]) {
        hierarchy[department] = [];
      }
      
      // Only add the assignee to its department if it's not the same as the department
      // or if it's the only assignee for that department
      if (assignee !== department) {
        hierarchy[department].push(assignee);
      }
    });
    
    // After populating, check if a department has no assignees (because we filtered out itself)
    // If so, add it back as its own assignee
    Object.keys(hierarchy).forEach(dept => {
      if (hierarchy[dept].length === 0 && uniqueValues.assignee.includes(dept)) {
        hierarchy[dept].push(dept);
      }
    });
    
    // Sort assignees within each department
    Object.keys(hierarchy).forEach(dept => {
      hierarchy[dept].sort();
    });
    
    return {
      hierarchy,
      // We don't have a concept of multi-department assignees like we do with orgs,
      // but including this property to match the getOrgHierarchy structure
      multipleDeptAssignees: []
    };
  }, [uniqueValues.assignee]);
  
  // Add a function to toggle department expansion
  const toggleDepartmentExpansion = (department) => {
    setExpandedDepartments(prev => ({
      ...prev,
      [department]: !prev[department]
    }));
  };
  
  // Add a function to handle toggling a whole department
  const toggleDepartment = (department) => {
    // Get all assignees under this department
    const assigneesUnderDept = getAssigneeHierarchy?.hierarchy?.[department] || [];
      
    setSelectedAssignees(prev => {
      // Check if all assignees AND the department itself are selected
      const isDepartmentSelected = assigneesUnderDept.every(assignee => 
        prev.includes(assignee)
      ) && prev.includes(department);
      
      let newSelection;
      if (isDepartmentSelected) {
        // If all assignees in this department (including department itself) are already selected, deselect them
        newSelection = prev.filter(assignee => 
          !assigneesUnderDept.includes(assignee) && assignee !== department
        );
      } else {
        // Otherwise, add all missing assignees from this department AND the department itself
        newSelection = [...prev];
        
        // Add the department itself if it's not already in the selection
        if (!newSelection.includes(department)) {
          newSelection.push(department);
        }
        
        // Add all assignees from the department if not already included
        assigneesUnderDept.forEach(assignee => {
          if (!newSelection.includes(assignee)) {
            newSelection.push(assignee);
          }
        });
      }
      
      // Update the filters object
      setFilters(prevFilters => ({
        ...prevFilters,
        assignee: newSelection.length > 0 ? newSelection : undefined
      }));
      
      return newSelection;
    });
  };
  
  // Add filtered assignee hierarchy logic
  const filteredAssigneeHierarchy = useMemo(() => {
    if (!getAssigneeHierarchy || !getAssigneeHierarchy.hierarchy) {
      return {};
    }
    
    const { hierarchy } = getAssigneeHierarchy;
    
    if (!assigneeSearchQuery) {
      // If no search query, return the full hierarchy
      return hierarchy;
    }
    
    const result = {};
    const searchLower = assigneeSearchQuery.toLowerCase();
    
    // Filter both departments and assignees
    Object.keys(hierarchy).forEach(dept => {
      // Check if department matches search
      const deptMatches = dept.toLowerCase().includes(searchLower);
      
      // Filter assignees in this department
      const matchingAssignees = hierarchy[dept].filter(assignee => 
        assignee.toLowerCase().includes(searchLower)
      );
      
      // Include this department if either the department matches or it has matching assignees
      if (deptMatches || matchingAssignees.length > 0) {
        // If department matches, include all its assignees
        // Otherwise, only include the matching assignees
        result[dept] = deptMatches ? hierarchy[dept] : matchingAssignees;
        
        // Auto-expand this department when there are search results
        if (matchingAssignees.length > 0 || deptMatches) {
          setExpandedDepartments(prev => ({
            ...prev,
            [dept]: true
          }));
        }
      }
    });
    
    return result;
  }, [getAssigneeHierarchy, assigneeSearchQuery]);
  
  // Reset expanded departments when closing the dropdown
  useEffect(() => {
    if (!isAssigneeDropdownOpen) {
      // Reset expanded states when closing the dropdown
      if (assigneeSearchQuery) {
        setAssigneeSearchQuery('');
      } else {
        setExpandedDepartments({});
      }
    }
  }, [isAssigneeDropdownOpen, assigneeSearchQuery]); // Added assigneeSearchQuery as dependency

  // Loading state with progress bar
  const renderLoadingState = () => {
    // Calculate progress as a percentage (cap at 99% until fully loaded)
    const progressPercentage = Math.min(99, (loadedCount / totalCount) * 100);
    
    return (
      <LoadingOverlay>
        <ProgressBarContainer>
          <ProgressBarFill style={{ width: `${progressPercentage}%` }} />
        </ProgressBarContainer>
        <p>Loading data... {loadedCount} of {totalCount} recommendations</p>
      </LoadingOverlay>
    );
  };

  // Function to convert data to CSV and trigger download
  const downloadCSV = () => {
    // Define the columns we want to export (include all available fields)
    const columns = [
      'recommendation',
      'details',
      'justification',
      'fullText',
      'organization',
      'orgType',
      'topic',
      'assignee',
      'document',
      'url'
    ];
    
    // Create header row
    const headerRow = columns.map(col => {
      // Capitalize first letter of each column name
      return col.charAt(0).toUpperCase() + col.slice(1);
    });
    
    // Process data rows
    const dataRows = filteredData.map(item => {
      return columns.map(col => {
        let value = item[col];
        
        // Handle arrays (like topics)
        if (Array.isArray(value)) {
          value = value.join(', ');
        }
        
        // Convert to string and escape quotes
        const stringValue = value ? String(value).replace(/"/g, '""') : '';
        
        // Wrap in quotes to handle commas and line breaks
        return `"${stringValue}"`;
      });
    });
    
    // Combine header and data rows
    const csvContent = [
      headerRow.join(','),
      ...dataRows.map(row => row.join(','))
    ].join('\n');
    
    // Create a Blob containing the CSV data
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    
    // Create a download link and trigger click
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', 'recommendations.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };


  // Add a smaller version of the download button
  const CompactDownloadButton = styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 4px 8px;
    background-color: #e5e7eb;
    border: 1px solid #d1d5db;
    border-radius: 6px;
    font-size: 13px;
    font-weight: 500;
    color: #374151;
    cursor: pointer;
    transition: all 0.2s;
    gap: 4px;
    height: 28px;
    white-space: nowrap;
    margin-left: 8px;
    
    &:hover {
      background-color: #d1d5db;
    }
    
    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
      
      &:hover {
        background-color: #e5e7eb;
      }
    }
  `;

  // Handle toggling a similar recommendation in the multiselect
  const toggleSimilarRecommendation = (rec) => {
    // Make sure recommendation is trimmed
    const trimmedRec = rec.trim();
    
    // Check if we're adding (not removing) the recommendation
    const isAdding = !selectedSimilarRecommendations.includes(trimmedRec);
    
    setSelectedSimilarRecommendations(prev => {
      const newSelection = prev.includes(trimmedRec)
        ? prev.filter(r => r !== trimmedRec)
        : [...prev, trimmedRec];
      
      // Update the filters object
      setFilters(prevFilters => ({
        ...prevFilters,
        similarRecommendations: newSelection.length > 0 ? newSelection : undefined
      }));
      
      return newSelection;
    });

    // If we're adding a recommendation, set the grouping
    if (isAdding) {
      setGroupBy('similarRecommendations');
    }
  };

  // Handle clicking the "See similar recommendations" button
  const handleSimilarRecommendationsClick = (recommendations) => {
    // Convert to array if needed and handle semicolon-separated strings
    let recommendationsArray;
    if (Array.isArray(recommendations)) {
      recommendationsArray = recommendations.map(r => r.trim());
    } else if (typeof recommendations === 'string') {
      // Split on semicolons if it's a semicolon-separated string
      recommendationsArray = recommendations.includes(';') 
        ? recommendations.split(';').map(r => r.trim()).filter(r => r.length > 0)
        : [recommendations.trim()];
    } else {
      console.log('Invalid recommendations format:', recommendations);
      return;
    }
    
    // Clear all existing filters and selections
    setSelectedCategories([]);
    setSelectedAssignees([]);
    setSelectedOrgs([]);
    setSelectedOrgTypes([]);
    setSelectedSimilarRecommendations(recommendationsArray);
    
    // Reset all filters and set only the similarRecommendations filter
    setFilters({
      similarRecommendations: recommendationsArray
    });

    // Set grouping to similarRecommendations
    setGroupBy('similarRecommendations');
  };

  // Clear all selected similar recommendations
  const clearSimilarRecommendations = () => {
    setSelectedSimilarRecommendations([]);
    setFilters(prev => {
      const newFilters = { ...prev };
      delete newFilters.similarRecommendations;
      return newFilters;
    });
  };

  // Add click outside handler for the similarRecommendations dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        isSimilarRecDropdownOpen &&
        similarRecDropdownRef.current && 
        !similarRecDropdownRef.current.contains(event.target)
      ) {
        setIsSimilarRecDropdownOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isSimilarRecDropdownOpen]);

  // Add alignment recalculation for similar recommendations dropdown
  useEffect(() => {
    const computeAlignment = () => {
      const margin = 16; // min distance from viewport edge
      if (!similarRecDropdownRef?.current) return;
      
      const rect = similarRecDropdownRef.current.getBoundingClientRect();
      const menuWidth = 300; // matches dropdown width
      const viewportWidth = window.innerWidth;
      const spaceRight = viewportWidth - rect.right;
      const spaceLeft = rect.left;
      
      // Decide alignment: if we have enough room on the right, open to the right (align left edges).
      // Otherwise open to the left (align right edges).
      const openToRight = spaceRight >= menuWidth + margin || spaceRight >= spaceLeft;
      setSimilarRecAlignLeft(openToRight);
    };

    if (isSimilarRecDropdownOpen) {
      computeAlignment();
    }
    window.addEventListener('resize', computeAlignment);
    return () => window.removeEventListener('resize', computeAlignment);
  }, [isSimilarRecDropdownOpen]);

  // Get unique similar recommendations for dropdown
  const uniqueSimilarRecommendations = useMemo(() => {
    const allRecs = new Set();
    
    data.forEach(item => {
      if (item.similarRecommendations) {
        // Handle array or semicolon-separated string
        const recs = Array.isArray(item.similarRecommendations)
          ? item.similarRecommendations
          : typeof item.similarRecommendations === 'string'
            ? item.similarRecommendations.split(';').map(r => r.trim()).filter(Boolean)
            : [String(item.similarRecommendations)];
            
        recs.forEach(rec => allRecs.add(rec.trim()));
      }
    });
    
    return [...allRecs].sort();
  }, [data]);

  // Filtered similar recommendations based on search
  const filteredSimilarRecommendations = useMemo(() => {
    if (!similarRecSearchQuery) return uniqueSimilarRecommendations;
    
    return uniqueSimilarRecommendations.filter(rec => 
      rec.toLowerCase().includes(similarRecSearchQuery.toLowerCase())
    );
  }, [uniqueSimilarRecommendations, similarRecSearchQuery]);

  // Update the SimilarRecsButton styled component
  const SimilarRecsButton = styled.button`
    background-color: transparent;
    border: 1px solid #d1d5db;
    font-size: 13px;
    color: #6b7280;
    cursor: pointer;
    transition: background-color 0.2s;
    display: inline-flex;
    align-items: center;
    padding: 6px 8px;
    border-radius: 4px;
    opacity: 0;
    margin-top: 6px;
    max-width: 100%;
    white-space: normal;
    line-height: 1.2;
    text-align: left;
    
    &:hover {
      background-color: #e5e7eb;
      color: #374151;
    }
    
    .item-row:hover & {
      opacity: 1;
    }
  `;

  // Add table container ref and column widths state
  const tableContainerRef = useRef(null);

  // Add after getAssigneeHierarchy:

  // // Add this function to get the hierarchical topic data
  // const getTopicHierarchy = useMemo(() => {
  //   // The topicHierarchy is already in the correct format with topics mapping to arrays
  //   // We just need to ensure the recommendations are sorted
  //   const hierarchy = {};
    
  //   Object.entries(topicHierarchy).forEach(([topic, recommendations]) => {
  //     // Each topic already maps to an array of recommendations
  //     hierarchy[topic] = [...recommendations].sort();
  //   });
    
  //   return {
  //     hierarchy,
  //     multipleTopicRecs: []
  //   };
  // }, []);

  // Add a function to handle toggling a whole topic
  // const toggleTopic = (topic) => {
  //   setSelectedCategories(prev => {
  //     const newSelection = prev.includes(topic)
  //       ? prev.filter(cat => cat !== topic)
  //       : [...prev, topic];
      
  //     // Update the filters object
  //     setFilters(prevFilters => ({
  //       ...prevFilters,
  //       topic: newSelection.length > 0 ? newSelection : undefined
  //     }));
      
  //     return newSelection;
  //   });
  // };

  const DropdownSectionHeading = styled.div`
    padding: 8px 16px;
    color: #6b7280;
    font-size: 12px;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    background-color: #f9fafb;
    border-top: 1px solid #e5e7eb;
    border-bottom: 1px solid #e5e7eb;
    margin-top: ${props => props.$first ? '0' : '8px'};
  `;

  // Add filtered topics logic
  const filteredTopics = useMemo(() => {
    if (!topicSearchQuery) {
      return Object.keys(topicHierarchy).sort();
    }
    
    const searchLower = topicSearchQuery.toLowerCase();
    return Object.keys(topicHierarchy)
      .filter(topic => topic.toLowerCase().includes(searchLower))
      .sort();
  }, [topicSearchQuery]);

  return (
    <Container isSmallScreen={isSmallScreen}>
      <ControlsContainer>
        {/* Search Bar Row (Full Width) */}
        <div className="search-bar-row">
          {/* Search bar (expands to fill available space) */}
          <div style={{ 
            position: 'relative', 
            flex: "1 1 auto",
            minWidth: isSmallScreen ? "100%" : "250px"
          }}>
            <SearchIconWrapper style={{ top: 'calc(50% - 2px)' }}>
              <Search size={20} />
            </SearchIconWrapper>
            <SearchInput
              type="text"
              placeholder="Search recommendations"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{ 
                height: "42px",
                padding: "10px 16px 10px 48px",
                fontSize: "14px"
              }}
            />
            {searchQuery && (
              <SearchClearButton onClick={() => setSearchQuery('')}>
                <X size={16} />
              </SearchClearButton>
            )}
          </div>

          {/* Filter dropdowns container */}
          <div style={{
            display: 'flex',
            gap: '12px',
            flexShrink: 0,
            flexWrap: isSmallScreen ? 'wrap' : 'nowrap',
            justifyContent: isSmallScreen ? 'space-between' : 'flex-start',
            width: isSmallScreen ? '100%' : 'auto'
          }}>
            {/* Assignee filter dropdown */}
            <div style={{ 
              position: 'relative',
              flex: isSmallScreen ? '1 1 calc(50% - 6px)' : '0 0 auto',
              maxWidth: isSmallScreen ? 'calc(50% - 6px)' : 'none'
            }} ref={assigneeDropdownRef}>
              <DropdownButton 
                onClick={() => setIsAssigneeDropdownOpen(!isAssigneeDropdownOpen)}
                $active={selectedAssignees.length > 0}
                style={{ 
                  height: "42px",
                  minWidth: "120px",
                  padding: "0 12px",
                  fontSize: "14px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  width: isSmallScreen ? '100%' : 'auto'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  Assignee
                  {selectedAssignees.length > 0 && (
                    <span className="badge" style={{ marginLeft: "8px" }}>
                      {/* Count visible selected assignees, not including hidden departments */}
                      {selectedAssignees.filter(assignee => 
                        // Only count assignees that are visible to the user in the dropdown
                        // either directly or as selectable items within departments
                        (getAssigneeHierarchy?.hierarchy && 
                         Object.values(getAssigneeHierarchy.hierarchy).some(deptAssignees => 
                           deptAssignees.includes(assignee)
                         )) || 
                        // Or it's a department with a single self-assignee (so it appears directly)
                        (Object.keys(getAssigneeHierarchy?.hierarchy || {}).includes(assignee) && 
                         getAssigneeHierarchy?.hierarchy[assignee].length === 1 && 
                         getAssigneeHierarchy?.hierarchy[assignee][0] === assignee)
                      ).length}
                    </span>
                  )}
                </div>
                {selectedAssignees.length > 0 ? (
                  <span 
                    className="clear-button"
                    onClick={(e) => {
                      e.stopPropagation();
                      clearAssignees();
                    }}
                    style={{ marginLeft: "8px" }}
                  >
                    <X size={14} />
                  </span>
                ) : (
                  <ChevronDown size={16} style={{ marginLeft: "8px" }} />
                )}
              </DropdownButton>
              
              {isAssigneeDropdownOpen && (
                <FilterDropdownMenu $alignLeft={assigneeAlignLeft}>
                  <FilterItemsContainer>
                    <FilterSearchInput 
                      type="text"
                      placeholder="Search assignees"
                      value={assigneeSearchQuery}
                      onChange={(e) => setAssigneeSearchQuery(e.target.value)}
                    />
                    
                    {Object.keys(filteredAssigneeHierarchy).length === 0 ? (
                      <div style={{ color: '#6b7280', fontSize: '14px', padding: '4px 16px' }}>
                        No assignees found
                      </div>
                    ) : (
                      Object.keys(filteredAssigneeHierarchy).sort().map(department => {
                        const assignees = filteredAssigneeHierarchy[department];
                        const selectedAssigneesCount = assignees.filter(assignee => selectedAssignees.includes(assignee)).length;
                        const isDepartmentSelected = assignees.length > 0 && selectedAssigneesCount === assignees.length;
                        const isPartiallySelected = selectedAssigneesCount > 0 && selectedAssigneesCount < assignees.length;
                        const isExpanded = expandedDepartments[department] || assigneeSearchQuery || false;
                        
                        // Check if the only assignee is the department itself
                        const hasSingleSelfAssignee = assignees.length === 1 && assignees[0] === department;
                        
                        return (
                          <div key={department}>
                            <OrgTypeHeader 
                              onClick={() => hasSingleSelfAssignee ? toggleAssignee(department) : toggleDepartmentExpansion(department)}
                            >
                              <OrgTypeLabel>
                                <input 
                                  type="checkbox" 
                                  id={`dropdown-department-${department}`}
                                  checked={hasSingleSelfAssignee ? selectedAssignees.includes(department) : isDepartmentSelected}
                                  ref={el => {
                                    // Handle indeterminate state for "partially" selected departments
                                    if (el && !hasSingleSelfAssignee) {
                                      el.indeterminate = isPartiallySelected;
                                    }
                                  }}
                                  onChange={(e) => {
                                    e.stopPropagation();
                                    if (hasSingleSelfAssignee) {
                                      toggleAssignee(department);
                                    } else {
                                      toggleDepartment(department);
                                    }
                                  }}
                                  onClick={(e) => e.stopPropagation()}
                                  style={{ marginRight: '10px' }}
                                />
                                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                  {department}
                                  {!hasSingleSelfAssignee && (
                                    <OrgTypeCount>({selectedAssigneesCount}/{assignees.length})</OrgTypeCount>
                                  )}
                                </span>
                              </OrgTypeLabel>
                              
                              {!hasSingleSelfAssignee && (
                                <div style={{ marginLeft: '8px' }}>
                                  {isExpanded ? (
                                    <ChevronUp size={16} />
                                  ) : (
                                    <ChevronDown size={16} />
                                  )}
                                </div>
                              )}
                            </OrgTypeHeader>
                            
                            {!hasSingleSelfAssignee && isExpanded && (
                              <OrgsContainer>
                                {assignees.map(assignee => {
                                  const isAssigneeSelected = selectedAssignees.includes(assignee);
                                  
                                  return (
                                    <OrgItem 
                                      key={assignee}
                                      $selected={isAssigneeSelected}
                                    >
                                      <input 
                                        type="checkbox" 
                                        id={`dropdown-assignee-${assignee}`}
                                        checked={isAssigneeSelected}
                                        onChange={() => toggleAssignee(assignee)}
                                      />
                                      <label htmlFor={`dropdown-assignee-${assignee}`}>
                                        <span>{assignee}</span>
                                      </label>
                                    </OrgItem>
                                  );
                                })}
                              </OrgsContainer>
                            )}
                          </div>
                        );
                      })
                    )}
                  </FilterItemsContainer>
                </FilterDropdownMenu>
              )}
            </div>

            {/* Topic filter dropdown */}
            <div style={{ 
              position: 'relative',
              flex: isSmallScreen ? '1 1 calc(50% - 6px)' : '0 0 auto',
              maxWidth: isSmallScreen ? 'calc(50% - 6px)' : 'none'
            }} ref={topicDropdownRef}>
              <DropdownButton 
                onClick={() => setIsTopicDropdownOpen(!isTopicDropdownOpen)}
                $active={selectedCategories.length > 0}
                style={{ 
                  height: "42px",
                  minWidth: "120px",
                  padding: "0 12px",
                  fontSize: "14px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  width: isSmallScreen ? '100%' : 'auto'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  Topic
                  {selectedCategories.length > 0 && (
                    <span className="badge" style={{ marginLeft: "8px" }}>
                      {selectedCategories.length}
                    </span>
                  )}
                </div>
                {selectedCategories.length > 0 ? (
                  <span 
                    className="clear-button"
                    onClick={(e) => {
                      e.stopPropagation();
                      clearCategories();
                    }}
                    style={{ marginLeft: "8px" }}
                  >
                    <X size={14} />
                  </span>
                ) : (
                  <ChevronDown size={16} style={{ marginLeft: "8px" }} />
                )}
              </DropdownButton>
              
              {isTopicDropdownOpen && (
                <FilterDropdownMenu $alignLeft={topicAlignLeft}>
                  <FilterItemsContainer>
                    <FilterSearchInput 
                      type="text"
                      placeholder="Search topics"
                      value={topicSearchQuery}
                      onChange={(e) => setTopicSearchQuery(e.target.value)}
                    />
                    
                    {filteredTopics.length === 0 ? (
                      <div style={{ color: '#6b7280', fontSize: '14px', padding: '4px 16px' }}>
                        No topics found
                      </div>
                    ) : (
                      filteredTopics
                        .sort((a, b) => {
                          // First sort by selection status (selected items first)
                          if (selectedCategories.includes(a) && !selectedCategories.includes(b)) return -1;
                          if (!selectedCategories.includes(a) && selectedCategories.includes(b)) return 1;
                          // Then sort alphabetically
                          return a.localeCompare(b);
                        })
                        .map(topic => {
                          const isTopicSelected = selectedCategories.includes(topic);
                          return (
                            <FilterItem key={topic}>
                              <input 
                                type="checkbox" 
                                id={`dropdown-topic-${topic}`}
                                checked={isTopicSelected}
                                onChange={() => toggleTopicFilter(topic)}
                              />
                              <label htmlFor={`dropdown-topic-${topic}`}>
                                {topic}
                              </label>
                            </FilterItem>
                          );
                        })
                    )}
                  </FilterItemsContainer>
                </FilterDropdownMenu>
              )}
            </div>

            {/* Similar Recommendations dropdown */}
            <div style={{ 
              position: 'relative',
              flex: isSmallScreen ? '1 1 calc(50% - 6px)' : '0 0 auto',
              maxWidth: isSmallScreen ? 'calc(50% - 6px)' : 'none'
            }} ref={similarRecDropdownRef}>
              <DropdownButton 
                onClick={() => setIsSimilarRecDropdownOpen(!isSimilarRecDropdownOpen)}
                $active={selectedSimilarRecommendations.length > 0}
                style={{ 
                  height: "42px",
                  minWidth: "120px",
                  padding: "0 12px",
                  fontSize: "14px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  width: isSmallScreen ? '100%' : 'auto'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  Common recommendations
                  {selectedSimilarRecommendations.length > 0 && (
                    <span className="badge" style={{ marginLeft: "8px" }}>
                      {selectedSimilarRecommendations.length}
                    </span>
                  )}
                </div>
                {selectedSimilarRecommendations.length > 0 ? (
                  <span 
                    className="clear-button"
                    onClick={(e) => {
                      e.stopPropagation();
                      clearSimilarRecommendations();
                    }}
                    style={{ marginLeft: "8px" }}
                  >
                    <X size={14} />
                  </span>
                ) : (
                  <ChevronDown size={16} style={{ marginLeft: "8px" }} />
                )}
              </DropdownButton>
              
              {isSimilarRecDropdownOpen && (
                <FilterDropdownMenu $alignLeft={similarRecAlignLeft}>
                  <FilterItemsContainer>
                    <FilterSearchInput 
                      type="text"
                      placeholder="Search common recommendations"
                      value={similarRecSearchQuery}
                      onChange={(e) => setSimilarRecSearchQuery(e.target.value)}
                    />
                    
                    {filteredSimilarRecommendations.length === 0 ? (
                      <div style={{ color: '#6b7280', fontSize: '14px', padding: '4px 16px' }}>
                        No similar recommendations found
                      </div>
                    ) : (
                      filteredSimilarRecommendations
                        .sort((a, b) => {
                          // First sort by selection status (selected items first)
                          if (selectedSimilarRecommendations.includes(a) && !selectedSimilarRecommendations.includes(b)) return -1;
                          if (!selectedSimilarRecommendations.includes(a) && selectedSimilarRecommendations.includes(b)) return 1;
                          // Then sort alphabetically
                          return a.localeCompare(b);
                        })
                        .map(rec => {
                          const isSelected = selectedSimilarRecommendations.includes(rec);
                          return (
                            <FilterItem key={rec}>
                              <input 
                                type="checkbox" 
                                id={`dropdown-similar-${rec}`}
                                checked={isSelected}
                                onChange={() => toggleSimilarRecommendation(rec)}
                              />
                              <label htmlFor={`dropdown-similar-${rec}`}>
                                {rec}
                              </label>
                            </FilterItem>
                          );
                        })
                    )}
                  </FilterItemsContainer>
                </FilterDropdownMenu>
              )}
            </div>

            {/* Organization filter dropdown */}
            <div style={{ 
              position: 'relative',
              flex: isSmallScreen ? '1 1 calc(50% - 6px)' : '0 0 auto',
              maxWidth: isSmallScreen ? 'calc(50% - 6px)' : 'none'
            }} ref={orgDropdownRef}>
              <DropdownButton 
                onClick={() => setIsOrgDropdownOpen(!isOrgDropdownOpen)}
                $active={selectedOrgs.length > 0 || selectedOrgTypes.length > 0}
                style={{ 
                  height: "42px",
                  minWidth: "120px",
                  padding: "0 12px",
                  fontSize: "14px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  width: isSmallScreen ? '100%' : 'auto'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  Organization
                  {(selectedOrgs.length > 0 || selectedOrgTypes.length > 0) && (
                    <span className="badge" style={{ marginLeft: "8px" }}>
                      {selectedOrgs.length}
                    </span>
                  )}
                </div>
                {(selectedOrgs.length > 0 || selectedOrgTypes.length > 0) ? (
                  <span 
                    className="clear-button"
                    onClick={(e) => {
                      e.stopPropagation();
                      clearOrganizations();
                    }}
                    style={{ marginLeft: "8px" }}
                  >
                    <X size={14} />
                  </span>
                ) : (
                  <ChevronDown size={16} style={{ marginLeft: "8px" }} />
                )}
              </DropdownButton>
              
              {/* Use the orgDropdownContent variable we created earlier */}
              {orgDropdownContent}
            </div>

            {/* Group by dropdown */}
            <div style={{ 
              position: 'relative',
              flex: isSmallScreen ? '1 1 auto' : '0 0 auto',
              marginLeft: isSmallScreen ? '0' : 'auto',
              order: isSmallScreen ? '3' : '0'
            }}>
              <DropdownButton 
                ref={groupButtonRef}
                onClick={() => setIsGroupMenuOpen(!isGroupMenuOpen)}
                $active={!!groupBy}
                style={{ 
                  height: "42px",
                  minWidth: "120px",
                  padding: "0 12px",
                  fontSize: "14px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  width: isSmallScreen ? '100%' : 'auto'
                }}
              >
                {groupBy ? (
                  <>
                    <span style={{ display: 'flex', alignItems: 'center' }}>
                      Grouped by: {(() => {
                        switch(groupBy) {
                          case 'similarRecommendations':
                            return 'Similar recommendations';
                          case 'orgType':
                            return 'Organization type';
                          case 'assigneeParent':
                            return 'Agency';
                          default:
                            return groupBy.charAt(0).toUpperCase() + groupBy.slice(1);
                        }
                      })()}
                    </span>
                    <span 
                      className="clear-button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setGroupBy(null);
                      }}
                      style={{ marginLeft: "8px" }}
                    >
                      <X size={14} />
                    </span>
                  </>
                ) : (
                  <>
                    <span>Group by</span>
                    <ChevronDown size={16} style={{ marginLeft: "8px" }} />
                  </>
                )}
              </DropdownButton>
              
              {isGroupMenuOpen && (
                <DropdownMenu ref={groupMenuRef}>
                  <div>
                    {/* Topics section */}
                    <DropdownItem
                      key="topic"
                      className={groupBy === 'topic' ? 'selected' : ''}
                      onClick={() => {
                        setGroupBy('topic');
                        setIsGroupMenuOpen(false);
                      }}
                    >
                      By high-level topic
                    </DropdownItem>
                    
                    {/* Similar Recommendations section */}
                    <DropdownItem
                      key="similarRecommendations"
                      className={groupBy === 'similarRecommendations' ? 'selected' : ''}
                      onClick={() => {
                        setGroupBy('similarRecommendations');
                        setIsGroupMenuOpen(false);
                      }}
                    >
                      By similar recommendations
                    </DropdownItem>

                    {/* Assignees section */}
                    <DropdownSectionHeading>Assignees</DropdownSectionHeading>
                    <DropdownItem
                      key="assigneeParent"
                      className={groupBy === 'assigneeParent' ? 'selected' : ''}
                      onClick={() => {
                        setGroupBy('assigneeParent');
                        setIsGroupMenuOpen(false);
                      }}
                    >
                      By agency
                    </DropdownItem>
                    <DropdownItem
                      key="assignee"
                      className={groupBy === 'assignee' ? 'selected' : ''}
                      onClick={() => {
                        setGroupBy('assignee');
                        setIsGroupMenuOpen(false);
                      }}
                    >
                      By specific office
                    </DropdownItem>

                    {/* Submitters section */}
                    <DropdownSectionHeading>Submitters</DropdownSectionHeading>
                    <DropdownItem
                      key="orgType"
                      className={groupBy === 'orgType' ? 'selected' : ''}
                      onClick={() => {
                        setGroupBy('orgType');
                        setIsGroupMenuOpen(false);
                      }}
                    >
                      By organization type
                    </DropdownItem>
                    <DropdownItem
                      key="organization"
                      className={groupBy === 'organization' ? 'selected' : ''}
                      onClick={() => {
                        setGroupBy('organization');
                        setIsGroupMenuOpen(false);
                      }}
                    >
                      By specific organization
                    </DropdownItem>
                  </div>
                </DropdownMenu>
              )}
            </div>

          </div>
        </div>
        
        {/* Tag filter section with improved layout for mobile */}
        <FilterTagsContainer>
          {/* --- COMMENTED OUT Key Topics & Org Type Chips --- */}
          {/*
          {/* Category label for topics */}
          {/* <CategoryLabel $first>Key topics:</CategoryLabel> */}

          {/* Topic chips */}
          {/* {filterButtonCategories.map(topic => {
            const isActive = selectedCategories.includes(topic);
            const colors = getTagColors('topic', topic);
            return (
              <TagStyleFilterButton
                key={topic}
                $bgColor={colors.bg}
                $textColor={colors.text}
                $active={isActive}
                onClick={() => toggleTopic(topic)}
              >
                {topic}
                {isActive && (
                  <span className="clear-icon">
                    <X size={12} />
                  </span>
                )}
              </TagStyleFilterButton>
            );
          })} */}

          {/* Category label for org types */}
          {/* <CategoryLabel>Org type:</CategoryLabel> */}

          {/* Org type chips */}
          {/* {filterButtonOrgTypes.map(orgType => {
            const isActive = selectedOrgTypes.includes(orgType);
            const colors = getTagColors('orgType', orgType);
            return (
              <TagStyleFilterButton
                key={orgType}
                $bgColor={colors.bg}
                $textColor={colors.text}
                $active={isActive}
                onClick={() => toggleOrgType(orgType)}
              >
                {orgType}
                {isActive && (
                  <span className="clear-icon">
                    <X size={12} />
                  </span>
                )}
              </TagStyleFilterButton>
            );
          })} */}
          {/* --- END COMMENTED OUT --- */}
        </FilterTagsContainer>
        
        {/* Count text and clear filters */}
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <CountText style={{ 
            marginTop: '0px', 
            marginBottom: '0px',
            marginLeft: isSmallScreen ? '8px' : '18px',
            fontSize: isSmallScreen ? '13px' : '14px'
          }}>
            {filteredData.length} recommendation{filteredData.length !== 1 ? 's matching filters' : ' matches filters'}
            {(Object.keys(filters).length > 0 || searchQuery) && (
              <ClearFiltersLink onClick={() => {
                setFilters({});
                setSearchQuery('');
                setSelectedCategories([]);
                setSelectedAssignees([]);
                setSelectedOrgs([]);
                setSelectedOrgTypes([]);
                setSelectedSimilarRecommendations([]);
              }}>
                (clear filters)
              </ClearFiltersLink>
            )}
          </CountText>
          
          {/* Compact Download CSV button */}
          <CompactDownloadButton
            onClick={downloadCSV}
            title="Download recommendations as CSV"
            disabled={loading || filteredData.length === 0}
            style={{ 
              opacity: (loading || filteredData.length === 0) ? 0.6 : 1,
              cursor: (loading || filteredData.length === 0) ? 'not-allowed' : 'pointer',
            }}
          >
            <Download size={12} />
            <span>CSV</span>
          </CompactDownloadButton>
        </div>
        
        {/* Error message */}
        {error && (
          <ErrorMessage>
            <strong>Error:</strong> {error}
          </ErrorMessage>
        )}
        
        {/* Loading state with progress bar */}
        {loading ? (
          renderLoadingState()
        ) : (
          <>
            {/* Mobile Card View */}
            {isMobileView ? (
              <>
                <CardContainer>
                  {!groupBy ? (
                    // Regular ungrouped data for mobile
                    displayedData.map((item) => (
                      <Card key={item.id}>
                        <CardHeader>
                          <CardTitle>
                            {safeRenderValue(item.recommendation)}
                          </CardTitle>
                          <DetailsButton onClick={() => handleRowClick(item)}>
                            Details
                          </DetailsButton>
                        </CardHeader>
                        <CardTags>
                          {processTagList(safeRenderValue(item.organization), 'organization').map((org, index) => (
                            <Tag 
                              key={`org-${index}`}
                              $bgColor={getTagColors('organization', org).bg} 
                              $textColor={getTagColors('organization', org).text}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleTagFilter(org, 'organization');
                              }}
                            >
                              {org}
                            </Tag>
                          ))}
                        </CardTags>
                      </Card>
                    ))
                  ) : (
                    // Grouped data for mobile
                    Object.entries(displayedGroups).map(([group, items]) => (
                      <div key={group}>
                        <GroupSection>
                          <GroupHeaderBar 
                            onClick={() => toggleGroupCollapse(group)}
                            style={{
                              width: '100%',
                              display: 'flex',
                              justifyContent: 'space-between',
                              padding: '12px 16px',
                              boxSizing: 'border-box',
                              position: 'sticky',
                              top: 0,
                              zIndex: 9
                            }}
                          >
                            <GroupHeaderLeft>
                              {collapsedGroups[group] ? <ChevronRight size={16} /> : <ChevronDown size={16} />}
                              <Tag 
                                $bgColor={getTagColors(groupBy, group).bg} 
                                $textColor={getTagColors(groupBy, group).text} 
                                style={{ marginLeft: '8px' }}
                              >
                                <span>{group}</span>
                              </Tag>
                              <GroupCount style={{ marginLeft: '12px' }}>
                                ({items.length})
                              </GroupCount>
                            </GroupHeaderLeft>
                            <div style={{ width: '20px' }}>
                              {/* Empty div to maintain consistent flex layout */}
                            </div>
                          </GroupHeaderBar>
                          
                          {!collapsedGroups[group] && loadedGroups[group] && (
                            <div style={{ padding: '8px', overflow: 'visible', position: 'relative' }}>
                              {items.map((item) => (
                                <Card key={item.id} style={{ marginBottom: '8px' }}>
                                  <CardHeader>
                                    <CardTitle>
                                      {safeRenderValue(item.recommendation)}
                                    </CardTitle>
                                    <DetailsButton onClick={() => handleRowClick(item)}>
                                      Details
                                    </DetailsButton>
                                  </CardHeader>
                                  <CardTags>
                                    {processTagList(safeRenderValue(item.organization), 'organization').map((org, index) => (
                                      <Tag 
                                        key={`org-${index}`}
                                        $bgColor={getTagColors('organization', org).bg} 
                                        $textColor={getTagColors('organization', org).text}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleTagFilter(org, 'organization');
                                        }}
                                      >
                                        {org}
                                      </Tag>
                                    ))}
                                  </CardTags>
                                </Card>
                              ))}
                            </div>
                          )}
                        </GroupSection>
                      </div>
                    ))
                  )}
                </CardContainer>
              </>
            ) : (
              // Regular table view
              <>
                <div style={{ 
                  width: '100%',
                  display: 'block',
                  tableLayout: 'fixed',
                  borderCollapse: 'collapse',
                  marginBottom: '20px',
                  padding: 0
                }}>
                  <div style={{ 
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    backgroundColor: 'white',
                    overflow: 'visible',
                    marginBottom: '20px',
                    width: '100%',
                    display: 'table',
                    position: 'relative'
                  }} ref={tableContainerRef}>
                    {/* Table header */}
                    <div style={{ 
                      backgroundColor: '#f9fafb',
                      padding: '12px 16px',
                      borderBottom: '1px solid #e5e7eb',
                      display: 'flex',
                      fontWeight: '500',
                      position: 'sticky',
                      top: 0,
                      zIndex: 10,
                      width: '100%'
                    }}>
                      {(() => {
                        return (
                          <>
                            <ResizableHeaderCell 
                              width={adjustedWidths.recommendation}
                              onResize={() => {}} // Empty function as placeholder
                              onClick={() => handleSort('recommendation')}
                              className={sortConfig.key === 'recommendation' ? 'active-sort' : ''}
                            >
                              <div className="cell-content">
                                <span>Recommendation</span>
                                <span className={`sort-icon ${sortConfig.key === 'recommendation' ? `active-sort active-${sortConfig.direction}` : ''}`}>
                                  <span className="up-arrow">
                                    <ChevronUp size={14} strokeWidth={2.5} />
                                  </span>
                                  <span className="down-arrow">
                                    <ChevronDown size={14} strokeWidth={2.5} />
                                  </span>
                                </span>
                              </div>
                            </ResizableHeaderCell>
                            {groupBy !== 'organization' && (
                              <ResizableHeaderCell 
                                width={adjustedWidths.organization}
                                onResize={() => {}}
                                onClick={() => handleSort('organization')}
                              >
                                <div className="cell-content">
                                  <span>Organization</span>
                                  <span className={`sort-icon ${sortConfig.key === 'organization' ? `active-sort active-${sortConfig.direction}` : ''}`}>
                                    <span className="up-arrow">
                                      <ChevronUp size={12} />
                                    </span>
                                    <span className="down-arrow">
                                      <ChevronDown size={12} />
                                    </span>
                                  </span>
                                </div>
                              </ResizableHeaderCell>
                            )}
                            <ResizableHeaderCell 
                              width={adjustedWidths.details}
                              onResize={() => {}}
                              onClick={() => handleSort('details')}
                            >
                              <div className="cell-content">
                                <span>Summary</span>
                                <span className={`sort-icon ${sortConfig.key === 'details' ? `active-sort active-${sortConfig.direction}` : ''}`}>
                                  <span className="up-arrow">
                                    <ChevronUp size={14} strokeWidth={2.5} />
                                  </span>
                                  <span className="down-arrow">
                                    <ChevronDown size={14} strokeWidth={2.5} />
                                  </span>
                                </span>
                              </div>
                            </ResizableHeaderCell>
                            {groupBy !== 'topic' && (
                              <ResizableHeaderCell 
                                width={adjustedWidths.topic}
                                onResize={() => {}}
                                onClick={() => handleSort('topic')}
                              >
                                <div className="cell-content">
                                  <span>Topic</span>
                                  <span className={`sort-icon ${sortConfig.key === 'topic' ? `active-sort active-${sortConfig.direction}` : ''}`}>
                                    <span className="up-arrow">
                                      <ChevronUp size={12} />
                                    </span>
                                    <span className="down-arrow">
                                      <ChevronDown size={12} />
                                    </span>
                                  </span>
                                </div>
                              </ResizableHeaderCell>
                            )}
                            {groupBy !== 'assignee' && (
                              <ResizableHeaderCell 
                                width={adjustedWidths.assignee}
                                onResize={() => {}}
                                onClick={() => handleSort('assignee')}
                              >
                                <div className="cell-content">
                                  <span>Assignee</span>
                                  <span className={`sort-icon ${sortConfig.key === 'assignee' ? `active-sort active-${sortConfig.direction}` : ''}`}>
                                    <span className="up-arrow">
                                      <ChevronUp size={12} />
                                    </span>
                                    <span className="down-arrow">
                                      <ChevronDown size={12} />
                                    </span>
                                  </span>
                                </div>
                              </ResizableHeaderCell>
                            )}
                          </>
                        );
                      })()}
                    </div>
                    
                    {!groupBy ? (
                      // Regular table body for ungrouped data
                      <div style={{ padding: '0', position: 'relative' }}>
                        {filteredData.length === 0 ? (
                          <div style={{ padding: '40px 0', textAlign: 'center', color: '#6b7280' }}>
                            No results match your search criteria
                          </div>
                        ) : (
                          displayedData.map((item, index) => {
                            const isLastItem = index === displayedData.length - 1;
                            
                            return (
                              <ItemRow 
                                key={item.id} 
                                onClick={() => handleRowClick(item)}
                                isLastItem={isLastItem}
                                hasBulletPoints={countBulletPoints(safeRenderValue(item.recommendation) + safeRenderValue(item.details)) > 3}
                                className="item-row"
                              >
                                <div style={{ 
                                  width: `${adjustedWidths.recommendation}%`, 
                                  fontWeight: 500, 
                                  fontSize: '14px',
                                  color: '#1f2937', 
                                  padding: '0 16px 0 0',
                                  overflow: 'hidden',
                                  display: 'flex',
                                  flexDirection: 'column',
                                  lineHeight: '1.3',
                                  height: 'auto',
                                  minHeight: containsBulletPoints(safeRenderValue(item.recommendation)) ? '60px' : '40px'
                                }}>
                                  <TruncatedTextWithBullets
                                    text={safeRenderValue(item.recommendation)}
                                    maxLines={6}
                                    className="recommendation-text"
                                  />
                                  {item.similarRecommendations && item.similarRecommendations.length > 0 && (
                                    <SimilarRecsButton 
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleSimilarRecommendationsClick(item.similarRecommendations);
                                      }}
                                    >
                                      See similar recommendations
                                    </SimilarRecsButton>
                                  )}
                                </div>
                                {groupBy !== 'organization' && (
                                  <div style={{ 
                                    width: `${adjustedWidths.organization}%`, 
                                    fontSize: '14px', 
                                    padding: '0 16px',
                                    overflow: 'visible' // Change from 'hidden' to 'visible'
                                  }}>
                                    <SmartTagContainer
                                      tags={processTagList(safeRenderValue(item.organization), 'organization')}
                                      getColor={(org) => getTagColors('organization', org)}
                                      containerWidth={adjustedWidths.organization}
                                      maxHeight={88}
                                      maxTags={null}
                                      isSmallScreen={isSmallScreen}
                                      adjustForBullets={containsBulletPoints(safeRenderValue(item.details))}
                                      onTagClick={handleTagFilter}
                                      tagType="organization"
                                    />
                                  </div>
                                )}
                                <div style={{ 
                                  width: `${adjustedWidths.details}%`, 
                                  color: '#6b7280',
                                  overflow: 'hidden',
                                  fontSize: '14px',
                                  padding: '0 16px',
                                  lineHeight: '1.3',
                                  height: 'auto',
                                  minHeight: containsBulletPoints(safeRenderValue(item.details)) ? '60px' : '40px',
                                  display: '-webkit-box',
                                  WebkitLineClamp: containsBulletPoints(safeRenderValue(item.details)) ? 4 : 4,
                                  WebkitBoxOrient: 'vertical',
                                  textOverflow: 'ellipsis'
                                }}>
                                  <TruncatedTextWithBullets
                                    text={safeRenderValue(item.details)}
                                    maxLines={6}
                                    className="details-text"
                                  />
                                </div>
                                {groupBy !== 'topic' && (
                                  <div style={{ 
                                    width: `${adjustedWidths.topic}%`, 
                                    fontSize: '14px', 
                                    padding: '0 16px',
                                    overflow: 'visible', // Change from 'hidden' to 'visible'
                                    borderRight: '1px solid #e5e7eb', // Add right border to topic column
                                    height: '100%', // Ensure the border extends the full height
                                    maxWidth: `${adjustedWidths.topic}%`, // Add explicit max-width constraint
                                    boxSizing: 'border-box' // Ensure padding is included in width calculation
                                  }}>
                                    <SmartTagContainer
                                      tags={item.topic} 
                                      getColor={(cat) => getTagColors('topic', cat)}
                                      containerWidth={adjustedWidths.topic}
                                      maxHeight={88}
                                      maxTags={null}
                                      isSmallScreen={isSmallScreen}
                                      adjustForBullets={containsBulletPoints(safeRenderValue(item.details))}
                                      onTagClick={handleTagFilter}
                                      tagType="topic"
                                    />
                                  </div>
                                )}
                                {groupBy !== 'assignee' && (
                                  <div style={{ 
                                    width: `${adjustedWidths.assignee}%`, 
                                    fontSize: '14px', 
                                    padding: '0 0 0 16px',
                                    overflow: 'visible',
                                    display: 'flex',
                                    flexDirection: 'column'
                                  }}>
                                    <SmartTagContainer
                                      tags={processTagList(safeRenderValue(item.assignee), 'assignee')}
                                      getColor={(assignee) => getTagColors('assignee', assignee)}
                                      containerWidth={adjustedWidths.assignee}
                                      maxHeight={88}
                                      maxTags={null}
                                      isSmallScreen={isSmallScreen}
                                      adjustForBullets={containsBulletPoints(safeRenderValue(item.details))}
                                      onTagClick={handleTagFilter}
                                      tagType="assignee"
                                    />
                                  </div>
                                )}
                              </ItemRow>
                            );
                          })
                        )}
                      </div>
                    ) : (
                      // Group-based table body
                      <div className="grouped-table-container" style={{ 
                        display: 'block',
                        width: '100%',
                        minWidth: '800px',
                        border: 'none',
                        minHeight: '200px',
                        padding: '8px',
                        boxSizing: 'border-box',
                        position: 'relative',
                        overflow: 'visible'
                      }}>
                        {Object.entries(groupedData).length === 0 ? (
                          <div style={{ padding: '40px 0', textAlign: 'center', color: '#6b7280' }}>
                            No results match your search criteria
                          </div>
                        ) : (
                          Object.entries(displayedGroups).map(([group, items]) => {
                            const isCollapsed = collapsedGroups[group];
                            
                            return (
                              <GroupSection 
                                key={group} 
                                style={{ 
                                  marginBottom: '8px',
                                  width: '100%',
                                  minWidth: '800px',
                                  boxSizing: 'border-box',
                                  display: 'table',
                                  tableLayout: 'fixed'
                                }}
                              >
                                {/* Group header */}
                                <GroupHeaderBar 
                                  onClick={() => toggleGroupCollapse(group)}
                                  style={{
                                    width: '100%',
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    padding: '12px 16px',
                                    boxSizing: 'border-box',
                                    position: 'sticky',
                                    top: 0,
                                    zIndex: 9
                                  }}
                                >
                                  <GroupHeaderLeft>
                                    {isCollapsed ? <ChevronRight size={16} /> : <ChevronDown size={16} />}
                                    <Tag 
                                      $bgColor={getTagColors(groupBy, group).bg} 
                                      $textColor={getTagColors(groupBy, group).text} 
                                      style={{ marginLeft: '8px' }}
                                    >
                                      <span>{group}</span>
                                    </Tag>
                                    <GroupCount style={{ marginLeft: '12px' }}>
                                      ({items.length})
                                    </GroupCount>
                                  </GroupHeaderLeft>
                                  <div style={{ width: '20px' }}>
                                    {/* Empty div to maintain consistent flex layout */}
                                  </div>
                                </GroupHeaderBar>
                                
                                {/* Group items - only show if not collapsed and has been loaded */}
                                {!isCollapsed && (
                                  <>
                                    {loadingGroups[group] && (
                                      <div style={{ 
                                        width: '100%', 
                                        padding: '20px', 
                                        textAlign: 'center', 
                                        display: 'flex',
                                        justifyContent: 'center'
                                      }}>
                                        <Loader size={28} />
                                      </div>
                                    )}
                                    
                                    {loadedGroups[group] && (
                                      <div style={{ padding: '8px', overflow: 'visible', position: 'relative' }}>
                                        {items.map((item) => (
                                          <ItemRow 
                                            key={item.id} 
                                            onClick={() => handleRowClick(item)}
                                            hasBulletPoints={countBulletPoints(safeRenderValue(item.recommendation) + safeRenderValue(item.details)) > 3}
                                            className="item-row"
                                          >
                                            <div style={{ 
                                              width: `${adjustedWidths.recommendation}%`, 
                                              fontWeight: 500, 
                                              fontSize: '14px',
                                              color: '#1f2937', 
                                              padding: '0 16px 0 0',
                                              overflow: 'hidden',
                                              display: 'flex',
                                              flexDirection: 'column',
                                              lineHeight: '1.3',
                                              height: 'auto',
                                              minHeight: containsBulletPoints(safeRenderValue(item.recommendation)) ? '60px' : '40px'
                                            }}>
                                              <TruncatedTextWithBullets 
                                                text={safeRenderValue(item.recommendation)} 
                                                maxLines={6}
                                                className="recommendation-text"
                                              />
                                              {item.similarRecommendations && item.similarRecommendations.length > 0 && (
                                                <SimilarRecsButton 
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleSimilarRecommendationsClick(item.similarRecommendations);
                                                  }}
                                                >
                                                  Similar recommendations
                                                </SimilarRecsButton>
                                              )}
                                            </div>
                                            
                                            {groupBy !== 'organization' && (
                                              <div style={{ 
                                                width: `${adjustedWidths.organization}%`, 
                                                fontSize: '14px', 
                                                padding: '0 16px',
                                                overflow: 'visible'
                                              }}>
                                                <SmartTagContainer 
                                                  tags={processTagList(safeRenderValue(item.organization), 'organization')}
                                                  getColor={(tag) => getTagColors('organization', tag)}
                                                  maxHeight={adjustedWidths.organization > 15 ? 120 : 60}
                                                  containerWidth={`${adjustedWidths.organization}%`}
                                                  onTagClick={(tag, e) => {
                                                    e.stopPropagation();
                                                    handleTagFilter(tag, 'organization');
                                                  }}
                                                  tagType="organization"
                                                />
                                              </div>
                                            )}

                                            <div style={{ 
                                              width: `${adjustedWidths.details}%`, 
                                              color: '#6b7280',
                                              overflow: 'hidden',
                                              fontSize: '14px',
                                              padding: '0 16px',
                                              lineHeight: '1.3',
                                              height: 'auto',
                                              minHeight: containsBulletPoints(safeRenderValue(item.details)) ? '60px' : '40px',
                                              display: '-webkit-box',
                                              WebkitLineClamp: containsBulletPoints(safeRenderValue(item.details)) ? 4 : 4,
                                              WebkitBoxOrient: 'vertical',
                                              textOverflow: 'ellipsis'
                                            }}>
                                              <TruncatedTextWithBullets 
                                                text={safeRenderValue(item.details)} 
                                                maxLines={6}
                                                className="details-text"
                                              />
                                            </div>
                                            
                                            {groupBy !== 'topic' && (
                                              <div style={{ 
                                                width: `${adjustedWidths.topic}%`, 
                                                fontSize: '14px', 
                                                padding: '0 16px',
                                                overflow: 'visible',
                                                borderRight: '1px solid #e5e7eb',
                                                height: '100%',
                                                maxWidth: `${adjustedWidths.topic}%`,
                                                boxSizing: 'border-box'
                                              }}>
                                                <SmartTagContainer 
                                                  tags={processTagList(safeRenderValue(item.topic), 'topic')}
                                                  getColor={(tag) => getTagColors('topic', tag)}
                                                  maxHeight={60}
                                                  containerWidth={`${adjustedWidths.topic}%`}
                                                  onTagClick={(tag, e) => {
                                                    e.stopPropagation();
                                                    handleTagFilter(tag, 'topic');
                                                  }}
                                                  tagType="topic"
                                                />
                                              </div>
                                            )}
                                            
                                            {groupBy !== 'assignee' && (
                                              <div style={{ 
                                                width: `${adjustedWidths.assignee}%`, 
                                                fontSize: '14px', 
                                                padding: '0 0 0 16px',
                                                overflow: 'visible',
                                                display: 'flex',
                                                flexDirection: 'column'
                                              }}>
                                                <SmartTagContainer 
                                                  tags={processTagList(safeRenderValue(item.assignee), 'assignee')}
                                                  getColor={(tag) => getTagColors('assignee', tag)}
                                                  maxHeight={60}
                                                  containerWidth={`${adjustedWidths.assignee}%`}
                                                  onTagClick={(tag, e) => {
                                                    e.stopPropagation();
                                                    handleTagFilter(tag, 'assignee');
                                                  }}
                                                  tagType="assignee"
                                                />
                                              </div>
                                            )}
                                          </ItemRow>
                                        ))}
                                      </div>
                                    )}
                                  </>
                                )}
                              </GroupSection>
                            );
                          })
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}
            
            {/* Shared load more indicator */}
            <div 
              ref={loadMoreRef}
              style={{
                textAlign: 'center',
                padding: '20px',
                color: '#6b7280',
                marginTop: '10px',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center'
              }}
            >
              {isLoadingMore ? (
                <Loader size={28} />
              ) : hasMoreItems ? (
                <div style={{ height: '28px', width: '28px' }}></div>
              ) : (
                <div style={{ height: '28px', width: '28px' }}></div>
              )}
            </div>
          </>
        )}
      </ControlsContainer>
      
      {/* Detail panel and overlay */}
      <DetailPanelOverlay 
        isVisible={!!selectedRecord} 
        onClick={() => setSelectedRecord(null)}
      />
      <DetailPanel 
        ref={detailPanelRef} 
        isVisible={!!selectedRecord}
      >
        {selectedRecord ? (
          <>
            <CloseButton onClick={() => setSelectedRecord(null)}>
              <X size={24} />
            </CloseButton>
            
            <DetailTitle>{safeRenderValue(selectedRecord.recommendation)}</DetailTitle>
            
            <DetailSection>
              <DetailLabel>Organization</DetailLabel>
              <div style={{ 
                display: 'flex', 
                flexWrap: 'wrap', 
                gap: '8px', 
                maxHeight: '200px', 
                overflow: 'auto',
                padding: '4px 0'
              }}>
                {processTagList(safeRenderValue(selectedRecord.organization), 'organization').map((org, index) => (
                  <Tag 
                    key={`org-${index}`}
                    $bgColor={getTagColors('organization', org).bg} 
                    $textColor={getTagColors('organization', org).text}
                    $nowrap={false}
                    tagType="organization"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleTagFilter(org, 'organization');
                    }}
                  >
                    {org}
                  </Tag>
                ))}
              </div>
            </DetailSection>
            
            {selectedRecord.orgType && (
              <DetailSection>
                <DetailLabel>Organization Type</DetailLabel>
                <div style={{ 
                  display: 'flex', 
                  flexWrap: 'wrap', 
                  gap: '8px',
                  padding: '4px 0'
                }}>
                  <Tag 
                    $bgColor={getTagColors('orgType', selectedRecord.orgType).bg}
                    $textColor={getTagColors('orgType', selectedRecord.orgType).text}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleTagFilter(String(selectedRecord.orgType).trim(), 'orgType');
                    }}
                  >
                    {safeRenderValue(selectedRecord.orgType)}
                  </Tag>
                </div>
              </DetailSection>
            )}
            
            {selectedRecord.topic && (
              <DetailSection>
                <DetailLabel>Topic</DetailLabel>
                <DetailValue>
                  <div style={{ 
                    display: 'flex', 
                    flexWrap: 'wrap', 
                    gap: '8px',
                    marginTop: '8px'
                  }}>
                    {(Array.isArray(selectedRecord.topic) 
                      ? selectedRecord.topic 
                      : processTagList(safeRenderValue(selectedRecord.topic), 'topic')
                    ).map((topic, index) => (
                      <Tag 
                        key={index}
                        $bgColor={getTagColors('topic', topic).bg}
                        $textColor={getTagColors('topic', topic).text}
                        $nowrap={false}
                        tagType="topic"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleTagFilter(topic, 'topic');
                        }}
                      >
                        {topic}
                      </Tag>
                    ))}
                  </div>
                </DetailValue>
              </DetailSection>
            )}
            
            <DetailSection>
              <DetailLabel>Summary</DetailLabel>
              <DetailValue>
                <CollapsibleText>
                  {formatBulletPoints(safeRenderValue(selectedRecord.details))}
                </CollapsibleText>
              </DetailValue>
            </DetailSection>
            
            <DetailSection>
              <DetailLabel>Justification</DetailLabel>
              <DetailValue>
                <CollapsibleText>
                  {formatBulletPoints(safeRenderValue(selectedRecord.justification))}
                </CollapsibleText>
              </DetailValue>
            </DetailSection>
            
            <DetailSection>
              <DetailLabel>Full text</DetailLabel>
              <DetailValue>
                <CollapsibleText>
                  {(() => {
                    // Special handling for fullText to ensure all bullet points are properly formatted
                    let text = safeRenderValue(selectedRecord.fullText);
                    
                    // Add additional preprocessing specifically for the bullet points in the full text
                    // This helps with cases where the bullet is part of a paragraph
                    text = text.replace(/\s+●\s+/g, '\n● ');
                    
                    return formatBulletPoints(text);
                  })()}
                </CollapsibleText>
              </DetailValue>
            </DetailSection>
            
            <DetailSection>
              <DetailLabel>Assignee</DetailLabel>
              <div style={{ 
                display: 'flex', 
                flexWrap: 'wrap', 
                gap: '8px', 
                maxHeight: '200px', 
                overflow: 'auto',
                padding: '4px 0'
              }}>
                {processTagList(safeRenderValue(selectedRecord.assignee), 'assignee').map((assignee, index) => (
                  <Tag 
                    key={`assignee-${index}`}
                    $bgColor={getTagColors('assignee', assignee).bg} 
                    $textColor={getTagColors('assignee', assignee).text}
                    $nowrap={false}
                    tagType="assignee"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleTagFilter(assignee, 'assignee');
                    }}
                  >
                    {assignee}
                  </Tag>
                ))}
              </div>
            </DetailSection>
            
            {selectedRecord.document && (
            <DetailSection>
                <DetailLabel>Document</DetailLabel>
                <DetailValue style={{ 
                  wordBreak: 'break-all',
                  overflowWrap: 'break-word',
                  maxWidth: '100%'
                }}>
                  <a 
                    href={safeRenderValue(selectedRecord.document)} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    style={{
                      color: '#ae7cef',
                      textDecoration: 'none',
                      '&:hover': {
                        textDecoration: 'underline'
                      }
                    }}
                  >
                    {safeRenderValue(selectedRecord.document)}
                  </a>
                </DetailValue>
            </DetailSection>
            )}
            
            {/* Add Similar Recommendations section */}
            {selectedRecord.similarRecommendations && (
              <DetailSection>
                <DetailLabel>Similar Recommendations</DetailLabel>
                <DetailValue>
                  <SmartTagContainer
                    tags={processTagList(selectedRecord.similarRecommendations, 'similarRecommendations')} 
                    getColor={() => getTagColors('similarRecommendations')}
                    maxHeight={200}
                    adjustForBullets={false}
                    onTagClick={handleTagFilter}
                    tagType="similarRecommendations"
                  />
                </DetailValue>
              </DetailSection>
            )}
            
            {/* Add the Action Plan button */}
            <ActionPlanButton 
              href={selectedRecord.url ? safeRenderValue(selectedRecord.url) : '#'}
              target="_blank"
              rel="noopener noreferrer"
              disabled={!selectedRecord.url}
              onClick={e => !selectedRecord.url && e.preventDefault()}
            >
              View action plan
            </ActionPlanButton>
          </>
        ) : null}
      </DetailPanel>
    </Container>
  );
};


export default AIActionPlanDatabase;